/**
 * Properties & Inventory screen (M1) — the flagship management UI.
 *
 * A fully functional operator workflow layered top-down:
 *   1. Create a property + a properties table. Selecting a property drills in.
 *   2. For the selected property: units list + create-unit form.
 *   3. For the selected unit: rate plans list + create-rate-plan form,
 *      and a 14-day availability editor.
 *
 * All backend access goes through the fetch-based `../services.js` layer (web
 * boundary: no backend runtime import). Money is displayed via `formatMoney`
 * (minor units → localized currency) and decimal inputs are converted to minor
 * units with `parseMoneyToMinor`. API error envelopes are surfaced through the
 * shared accessible {@link UserMessage} component.
 *
 * SSR-safe: all network access happens inside effects/handlers; date math for
 * the availability window uses `Date` inside effects/handlers only, so the
 * initial render is pure.
 */
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactElement,
} from "react";
import {
  UserMessage,
  toUserMessage,
  type UserMessageModel,
} from "../../components/UserMessage.js";
import {
  apiArchiveUnit,
  apiCreateProperty,
  apiCreateRatePlan,
  apiCreateUnit,
  apiDeleteUnit,
  apiGeocode,
  apiGetProperty,
  apiGetUnit,
  apiListAvailability,
  apiListProperties,
  apiListRatePlans,
  apiListUnits,
  apiPropertyDistribution,
  apiReconcileInventory,
  apiRetryProvision,
  apiSetAvailability,
  apiSyncUnit,
  apiUpdateProperty,
  apiUpdateUnit,
  apiRemoveProperty,
  apiRetirementAssessment,
  apiScheduleDecommission,
  apiCancelScheduledDecommission,
  apiResolveDirectBookings,
  apiListArchivedProperties,
  apiArchivedPropertyDetail,
  asError,
  formatMoney,
  parseMoneyToMinor,
  type AvailabilityDay,
  type ArchivedPropertyDetail,
  type ArchivedPropertyRow,
  type ArchivedReservationRow,
  type DistributionState,
  type DistributionStatus,
  type InventoryReconcileSummary,
  type Property,
  type PropertyDistribution,
  type PropertyFormInput,
  type RatePlan,
  type RemovalMode,
  type RetirementAssessment,
  type RetirementReservation,
  type Unit,
  type UnitMutationResult,
} from "../services.js";
import { useMe } from "../useMe.js";

/** Compute an ISO YYYY-MM-DD date string offset by `days` from today. */
function isoDateOffset(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

// ===========================================================================
// Inline SVG icon helpers — a single consistent 1.5–2px stroke, currentColor,
// decorative (aria-hidden). These replace every EMOJI / text-symbol that was
// standing in for a structural icon on this screen (directive 1/2/3/28). They
// are purely presentational: no behaviour, no API, no layout coupling.
// ===========================================================================

/** Shared SVG wrapper so every icon inherits the same stroke conventions. */
function iconSvg(
  size: number,
  children: ReactElement | readonly ReactElement[],
  strokeWidth = 1.75,
): ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** Right-pointing chevron (rotates to down when expanded, via CSS). */
function IconChevron({ size = 16 }: { readonly size?: number }): ReactElement {
  return iconSvg(size, <path d="M9 6l6 6-6 6" />, 2);
}

/** Plus / add. */
function IconPlus({ size = 16 }: { readonly size?: number }): ReactElement {
  return iconSvg(size, [
    <path key="h" d="M12 5v14" />,
    <path key="w" d="M5 12h14" />,
  ]);
}

/** Horizontal ellipsis (more actions / overflow). */
function IconMore({ size = 18 }: { readonly size?: number }): ReactElement {
  return iconSvg(size, [
    <circle key="a" cx="5" cy="12" r="1.1" fill="currentColor" stroke="none" />,
    <circle key="b" cx="12" cy="12" r="1.1" fill="currentColor" stroke="none" />,
    <circle key="c" cx="19" cy="12" r="1.1" fill="currentColor" stroke="none" />,
  ]);
}

/** Close (X). */
function IconClose({ size = 18 }: { readonly size?: number }): ReactElement {
  return iconSvg(size, [
    <path key="a" d="M6 6l12 12" />,
    <path key="b" d="M18 6L6 18" />,
  ]);
}

/** Neutral building/home glyph — replaces the per-type property EMOJI. */
function IconBuilding({ size = 18 }: { readonly size?: number }): ReactElement {
  return iconSvg(size, [
    <path key="o" d="M4 20V6a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v14" />,
    <path key="r" d="M15 10h3a2 2 0 0 1 2 2v8" />,
    <path key="d" d="M9 20v-4h2v4" />,
    <path key="w1" d="M7 8h2" />,
    <path key="w2" d="M11 8h2" />,
    <path key="w3" d="M7 12h2" />,
    <path key="w4" d="M11 12h2" />,
  ]);
}

/** Check mark (completed step / confirmation). */
function IconCheck({ size = 16 }: { readonly size?: number }): ReactElement {
  return iconSvg(size, <path d="M5 12.5l4.2 4.2L19 7" />, 2);
}

/** Image placeholder (disabled media-upload affordance). */
function IconImage({ size = 20 }: { readonly size?: number }): ReactElement {
  return iconSvg(size, [
    <rect key="f" x="3" y="4" width="18" height="16" rx="2" />,
    <circle key="s" cx="8.5" cy="9.5" r="1.5" />,
    <path key="m" d="M21 16l-5-5L5 20" />,
  ]);
}

// ===========================================================================
// Property drawer (create + edit) — the two-tab slide-in form
// ===========================================================================

/** Selectable property types (drawer SELECT). */
const PROPERTY_TYPES = [
  "villa",
  "homestay",
  "boutique",
  "apartment",
  "hotel",
  "resort",
  "guest_house",
] as const;

/** Selectable currencies (drawer SELECT). */
const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AUD", "AED", "SGD", "THB"] as const;

/** Common ISO country codes (value = label = code). */
const COUNTRY_CODES = [
  "IN",
  "US",
  "GB",
  "AE",
  "SG",
  "TH",
  "AU",
  "FR",
  "ES",
  "IT",
] as const;

/** Minimum-stay handling: user-friendly label → wire value. */
const MIN_STAY_OPTIONS: readonly {
  readonly value: "both" | "arrival" | "through";
  readonly label: string;
}[] = [
  { value: "both", label: "Applies to both arrival and through nights" },
  { value: "arrival", label: "Applies to arrival night only" },
  { value: "through", label: "Applies to through nights only" },
];

/**
 * Friendly distribution status label + pill class (reused list + drawer).
 *
 * Stage-2 note: the backend now emits the reconciled 6-state set
 * (`draft`/`setup_required`/`provisioning`/`ready`/`sync_failed`/`archived`);
 * the legacy `not_ready`/`error` literals are retained only as aliases. Until
 * Stage 3 reworks these pills, the new states are mapped onto the existing
 * label/pill vocabulary (draft + setup_required → "Setup required"/pending,
 * sync_failed → "Sync issue"/critical, archived → "Archived"/pending).
 */
function distributionStatusLabel(state: DistributionState): string {
  switch (state) {
    case "draft":
    case "not_ready":
    case "setup_required":
      return "Setup required";
    case "provisioning":
      return "Provisioning";
    case "ready":
      return "Distribution ready";
    case "sync_failed":
    case "error":
      return "Sync issue";
    case "archived":
      return "Archived";
    default:
      return "Setup required";
  }
}

/** Pill class for a distribution state (reuses existing avos-status modifiers). */
function distributionStatusClass(state: DistributionState): string {
  switch (state) {
    case "draft":
    case "not_ready":
    case "setup_required":
    case "archived":
      return "avos-status avos-status--pending";
    case "provisioning":
      return "avos-status avos-status--attention";
    case "ready":
      return "avos-status avos-status--ok";
    case "sync_failed":
    case "error":
      return "avos-status avos-status--critical";
    default:
      return "avos-status avos-status--pending";
  }
}

/** A non-blocking post-save note describing the distribution outcome. */
interface DistributionNote {
  readonly severity: "success" | "info" | "warning";
  readonly message: UserMessageModel;
}

/**
 * Build the post-save distribution note from a returned status.
 *
 * Stage 3: the note must be TRUTHFUL for each of the reconciled 6 states.
 * A "draft" never claims distribution readiness; "sync_failed"/"error" surface
 * a retryable warning; "setup_required"/"not_ready" explain the remaining
 * setup; only "ready" renders the success-ready note.
 */
function buildDistributionNote(status: DistributionStatus): DistributionNote {
  switch (status.state) {
    case "draft":
      return {
        severity: "info",
        message: {
          what: "Saved as draft.",
          why: "The property is stored in AVOS only — it has not been pushed to the channel manager.",
          next: "Open the property and choose “Create property” when you’re ready to distribute.",
        },
      };
    case "sync_failed":
    case "error":
      return {
        severity: "warning",
        message: {
          what: "Saved in AVOS, but channel distribution needs attention.",
          why:
            status.reason ??
            status.lastError ??
            "The channel manager reported a provisioning error.",
          next: "Use “Retry sync” once the underlying issue is resolved.",
        },
      };
    case "provisioning":
      return {
        severity: "info",
        message: {
          what: "Saved in AVOS. Channel distribution is provisioning.",
          why: "The channel manager is setting the property up; this can take a moment.",
          next: "Refresh shortly to see the connected status.",
        },
      };
    case "setup_required":
    case "not_ready":
      return {
        severity: "info",
        message: {
          what: "Saved in AVOS. Complete setup to enable distribution.",
          why: "Distribution activates once the property has units and rate plans.",
          next: "Add units + rate plans from the property’s unit view.",
        },
      };
    case "archived":
      return {
        severity: "info",
        message: {
          what: "Saved. This property is archived.",
          why: "Archived properties are read-only and do not distribute.",
        },
      };
    case "ready":
    default:
      return {
        severity: "success",
        message: {
          what: "Saved in AVOS ✓ · Channex synced ✓",
          why: "The property is configured and connected for distribution.",
        },
      };
  }
}

/**
 * A compact, TRUTHFUL one-line save result for the edit drawer (directive M).
 * Never fabricates a Channex success: only "ready" claims "Channex synced ✓";
 * "sync_failed"/"error" show the failure (the Retry affordance lives in the
 * footer); draft/setup_required/provisioning claim only the AVOS save.
 */
function editSaveResultLine(state: DistributionState): {
  readonly text: string;
  readonly tone: "ok" | "critical" | "info";
} {
  switch (state) {
    case "ready":
      return { text: "Saved in AVOS ✓ · Channex synced ✓", tone: "ok" };
    case "sync_failed":
    case "error":
      return { text: "Saved in AVOS ✓ · Channex sync failed", tone: "critical" };
    case "provisioning":
      return { text: "Saved in AVOS ✓ · Channex provisioning…", tone: "info" };
    default:
      // draft / setup_required / not_ready / archived — no Channex claim.
      return { text: "Saved in AVOS ✓", tone: "info" };
  }
}

/** Which drawer tab is showing. The "danger" tab is edit-mode only (Feature A). */
type DrawerTab = "details" | "distribution" | "danger";

/** Editable form state held by the drawer while open. */
interface DrawerFormState {
  name: string;
  currency: string;
  type: string;
  email: string;
  phone: string;
  website: string;
  country: string;
  timezone: string;
  postcode: string;
  state: string;
  city: string;
  address: string;
  latitude: string;
  longitude: string;
  minPrice: string;
  maxPrice: string;
  autoupdateOnModification: boolean;
  autoupdateOnCancellation: boolean;
  minStayType: "both" | "arrival" | "through";
  inventoryDays: string;
  cutOffTime: string;
  cutOffDays: string;
  advanceEnabled: boolean;
  advanceDays: string;
}

/** The initial (create-mode) form state. Pure — no Date/window use. */
function emptyFormState(): DrawerFormState {
  return {
    name: "",
    currency: "INR",
    type: "villa",
    email: "",
    phone: "",
    website: "",
    country: "IN",
    timezone: "Asia/Kolkata",
    postcode: "",
    state: "",
    city: "",
    address: "",
    latitude: "",
    longitude: "",
    minPrice: "",
    maxPrice: "",
    autoupdateOnModification: true,
    autoupdateOnCancellation: true,
    minStayType: "both",
    inventoryDays: "500",
    cutOffTime: "00:00",
    cutOffDays: "0",
    advanceEnabled: false,
    advanceDays: "365",
  };
}

/** Map an existing property into drawer form state (edit-mode prefill). */
function formStateFromProperty(p: Property): DrawerFormState {
  const minStay: "both" | "arrival" | "through" =
    p.minStayType === "arrival" || p.minStayType === "through"
      ? p.minStayType
      : "both";
  return {
    name: p.name,
    currency: p.currency ?? "INR",
    type: p.type ?? "villa",
    email: p.email ?? "",
    phone: p.phone ?? "",
    website: p.website ?? "",
    country: p.country ?? "IN",
    timezone: p.timezone,
    postcode: p.postcode ?? "",
    state: p.state ?? "",
    city: p.city ?? "",
    address: p.address ?? "",
    latitude: p.latitude !== null ? String(p.latitude) : "",
    longitude: p.longitude !== null ? String(p.longitude) : "",
    minPrice:
      p.minPriceMinor !== null ? centsStringToDecimal(p.minPriceMinor) : "",
    maxPrice:
      p.maxPriceMinor !== null ? centsStringToDecimal(p.maxPriceMinor) : "",
    autoupdateOnModification: p.autoupdateOnModification,
    autoupdateOnCancellation: p.autoupdateOnCancellation,
    minStayType: minStay,
    inventoryDays: String(p.inventoryDays),
    cutOffTime: p.cutOffTime.slice(0, 5) || "00:00",
    cutOffDays: String(p.cutOffDays),
    advanceEnabled: p.maxAdvanceDays !== null,
    advanceDays: p.maxAdvanceDays !== null ? String(p.maxAdvanceDays) : "365",
  };
}

/** Convert a stored minor-unit string (e.g. "1200000") to a decimal string. */
function centsStringToDecimal(minor: string): string {
  const n = Number(minor);
  if (!Number.isFinite(n)) return "";
  return (n / 100).toString();
}

/** Required text fields keyed to the tab they live on, for validation focus. */
const REQUIRED_DETAIL_FIELDS: readonly {
  readonly key: keyof DrawerFormState;
  readonly label: string;
}[] = [
  { key: "name", label: "Property name" },
  { key: "currency", label: "Default currency" },
  { key: "type", label: "Property type" },
  { key: "email", label: "Property email" },
  { key: "phone", label: "Phone" },
  { key: "country", label: "Country" },
  { key: "timezone", label: "Timezone" },
  { key: "postcode", label: "Postal code" },
  { key: "state", label: "State / Region" },
  { key: "city", label: "City" },
  { key: "address", label: "Address" },
];

function PropertyDrawer({
  mode,
  propertyId,
  onClose,
  onSaved,
  onRemoved,
}: {
  readonly mode: "create" | "edit";
  readonly propertyId: string | null;
  readonly onClose: () => void;
  /**
   * Called after a successful save. `opts.keepOpen` is true for edit-mode saves
   * so the drawer stays open to show the compact, truthful sync-result line
   * (directive M); the parent still refreshes its list. Create/draft saves omit
   * it and the drawer closes.
   */
  readonly onSaved: (
    note: DistributionNote,
    opts?: { readonly keepOpen?: boolean },
  ) => void;
  /**
   * Called after a successful removal (decommission or hard delete).
   * `manualCleanupNote` carries the truthful Booking.com-extranet / remaining-
   * Channex-channel follow-up note when the removal needs manual cleanup.
   */
  readonly onRemoved: (
    outcome: "deleted" | "decommissioned",
    name: string,
    manualCleanupNote?: string | null,
  ) => void;
}): ReactElement {
  const [tab, setTab] = useState<DrawerTab>("details");
  // Create-mode wizard step. In edit mode the `tab` state drives free
  // navigation (details/distribution/danger); in create mode the step strip
  // doubles as a Step 1 → Step 2 indicator and `step` is authoritative.
  const [step, setStep] = useState<1 | 2>(1);
  const [form, setForm] = useState<DrawerFormState>(emptyFormState);
  // Snapshot of the initial form for the dirty-discard guard (create mode).
  const [initialForm] = useState<DrawerFormState>(emptyFormState);
  const [title, setTitle] = useState(mode === "edit" ? "Property" : "Add property");
  const [loading, setLoading] = useState(mode === "edit");
  const [busy, setBusy] = useState(false);
  const [draftBusy, setDraftBusy] = useState(false);
  const [apiError, setApiError] = useState<UserMessageModel | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [geoNote, setGeoNote] = useState<string | null>(null);
  const [geoError, setGeoError] = useState<UserMessageModel | null>(null);
  const [geoBusy, setGeoBusy] = useState(false);
  const [distState, setDistState] = useState<DistributionState | null>(null);
  const [retryBusy, setRetryBusy] = useState(false);
  // After a successful edit-save, a compact + TRUTHFUL per-state result line.
  const [saveResultState, setSaveResultState] = useState<DistributionState | null>(
    null,
  );

  // Edit-mode prefill (network in an effect → SSR-safe).
  useEffect(() => {
    if (mode !== "edit" || propertyId === null) return;
    let active = true;
    setLoading(true);
    apiGetProperty(propertyId)
      .then((res) => {
        if (!active) return;
        const err = asError(res);
        if (err !== null) {
          setApiError(toUserMessage(err));
          setLoading(false);
          return;
        }
        const ok = res as { property: Property; distribution: DistributionStatus };
        setForm(formStateFromProperty(ok.property));
        setTitle(ok.property.name);
        setDistState(ok.distribution.state);
        setLoading(false);
      })
      .catch(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [mode, propertyId]);

  /** True when the create-mode form has unsaved edits vs its initial snapshot. */
  const isDirty = useCallback((): boolean => {
    if (mode !== "create") return false;
    return (Object.keys(form) as (keyof DrawerFormState)[]).some(
      (k) => form[k] !== initialForm[k],
    );
  }, [mode, form, initialForm]);

  /**
   * Close with a dirty-discard guard. When the user has unsaved create-mode
   * edits, confirm before discarding (window.confirm — acceptable, minimal).
   * A clean form closes immediately.
   */
  const requestClose = useCallback((): void => {
    if (isDirty()) {
      const ok =
        typeof window === "undefined" ||
        window.confirm(
          "Discard this property? Your unsaved changes will be lost.",
        );
      if (!ok) return;
    }
    onClose();
  }, [isDirty, onClose]);

  // ESC closes the drawer (through the dirty-discard guard).
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") requestClose();
    }
    if (typeof window === "undefined") return;
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [requestClose]);

  // Body scroll lock while the drawer is open — no background scroll behind the
  // full-screen scrim. SSR-guarded; restores the prior overflow on unmount.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  function patch(next: Partial<DrawerFormState>): void {
    setForm((prev) => ({ ...prev, ...next }));
  }

  async function handleGeocode(): Promise<void> {
    setGeoError(null);
    setGeoNote(null);
    const query = [form.address, form.city, form.state, form.country]
      .map((s) => s.trim())
      .filter((s) => s !== "")
      .join(", ");
    if (query === "") {
      setGeoError({
        what: "Nothing to look up.",
        why: "Enter at least an address, city, state, or country first.",
        next: "Fill in some location details and try again.",
      });
      return;
    }
    setGeoBusy(true);
    try {
      const result = await apiGeocode(query);
      if (result === null) {
        setGeoError({
          what: "No location match found.",
          why: "The address could not be resolved to coordinates.",
          next: "Refine the address, or enter latitude/longitude manually.",
        });
        return;
      }
      setForm((prev) => ({
        ...prev,
        latitude: String(result.latitude),
        longitude: String(result.longitude),
        city: prev.city.trim() === "" && result.city !== null ? result.city : prev.city,
        state:
          prev.state.trim() === "" && result.state !== null ? result.state : prev.state,
        postcode:
          prev.postcode.trim() === "" && result.postcode !== null
            ? result.postcode
            : prev.postcode,
        country:
          prev.country.trim() === "" && result.country !== null
            ? result.country
            : prev.country,
      }));
      setGeoNote(result.displayName ?? "Coordinates updated.");
    } catch {
      setGeoError({
        what: "Location lookup failed.",
        why: "The geocoding request could not be completed.",
        next: "Try again, or enter latitude/longitude manually.",
      });
    } finally {
      setGeoBusy(false);
    }
  }

  /** Assemble the PropertyFormInput from the current form state. */
  function buildInput(): PropertyFormInput {
    const lat = form.latitude.trim();
    const lon = form.longitude.trim();
    return {
      name: form.name.trim(),
      currency: form.currency,
      type: form.type,
      email: form.email.trim(),
      phone: form.phone.trim(),
      ...(form.website.trim() !== "" ? { website: form.website.trim() } : {}),
      country: form.country,
      timezone: form.timezone.trim(),
      postcode: form.postcode.trim(),
      state: form.state.trim(),
      city: form.city.trim(),
      address: form.address.trim(),
      latitude: lat !== "" && Number.isFinite(Number(lat)) ? Number(lat) : null,
      longitude: lon !== "" && Number.isFinite(Number(lon)) ? Number(lon) : null,
      minPrice: form.minPrice.trim(),
      maxPrice: form.maxPrice.trim(),
      autoupdateOnModification: form.autoupdateOnModification,
      autoupdateOnCancellation: form.autoupdateOnCancellation,
      minStayType: form.minStayType,
      inventoryDays: Number(form.inventoryDays) || 500,
      cutOffTime: `${(form.cutOffTime || "00:00").slice(0, 5)}:00`,
      cutOffDays: Number(form.cutOffDays) || 0,
      maxAdvanceDays: form.advanceEnabled ? Number(form.advanceDays) || 365 : null,
    };
  }

  /** Validate all required Step-1 (Property details) fields. */
  function validateDetails(): boolean {
    for (const f of REQUIRED_DETAIL_FIELDS) {
      const value = form[f.key];
      if (typeof value === "string" && value.trim() === "") {
        setTab("details");
        setStep(1);
        setFieldError(`${f.label} is required.`);
        return false;
      }
    }
    setFieldError(null);
    return true;
  }

  function validate(): boolean {
    return validateDetails();
  }

  /**
   * Minimal validation for "Save as draft": a draft does NOT require the full
   * distribution set, but it still needs a name and a timezone so it can be
   * identified and later provisioned.
   */
  function validateDraft(): boolean {
    if (form.name.trim() === "") {
      setTab("details");
      setStep(1);
      setFieldError("Property name is required.");
      return false;
    }
    if (form.timezone.trim() === "") {
      setTab("details");
      setStep(1);
      setFieldError("Timezone is required.");
      return false;
    }
    setFieldError(null);
    return true;
  }

  /** Advance from Step 1 → Step 2 (create mode). Never provisions. */
  function handleNext(): void {
    setApiError(null);
    if (!validateDetails()) return;
    setStep(2);
    setTab("distribution");
  }

  /** Go back from Step 2 → Step 1 (create mode). */
  function handleBack(): void {
    setStep(1);
    setTab("details");
  }

  /** Save as draft: local-only create, distribution_state='draft', no provisioning. */
  async function handleSaveDraft(): Promise<void> {
    setApiError(null);
    if (!validateDraft()) return;
    setDraftBusy(true);
    const result = await apiCreateProperty({ ...buildInput(), draft: true });
    setDraftBusy(false);
    const err = asError(result);
    if (err !== null) {
      setApiError(toUserMessage(err));
      return;
    }
    const ok = result as { id: string; distribution: DistributionStatus };
    onSaved(buildDistributionNote(ok.distribution));
  }

  async function handleSave(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setApiError(null);
    if (!validate()) return;
    const input = buildInput();
    setBusy(true);
    if (mode === "create") {
      const result = await apiCreateProperty(input);
      setBusy(false);
      const err = asError(result);
      if (err !== null) {
        setApiError(toUserMessage(err));
        return;
      }
      const ok = result as { id: string; distribution: DistributionStatus };
      onSaved(buildDistributionNote(ok.distribution));
      return;
    }
    // edit mode
    if (propertyId === null) {
      setBusy(false);
      return;
    }
    const result = await apiUpdateProperty(propertyId, input);
    setBusy(false);
    const err = asError(result);
    if (err !== null) {
      setApiError(toUserMessage(err));
      return;
    }
    const ok = result as { property: Property; distribution: DistributionStatus };
    setDistState(ok.distribution.state);
    setSaveResultState(ok.distribution.state);
    // Keep the drawer open so the compact sync-result line is visible.
    onSaved(buildDistributionNote(ok.distribution), { keepOpen: true });
  }

  async function handleRetry(): Promise<void> {
    if (propertyId === null) return;
    setRetryBusy(true);
    try {
      const res = await apiRetryProvision(propertyId);
      setDistState(res.distribution.state);
      setSaveResultState(res.distribution.state);
    } finally {
      setRetryBusy(false);
    }
  }

  // Edit-mode submit label (create mode uses the wizard footer labels).
  const saveLabel = busy ? "Saving…" : "Save changes";

  return (
    <div
      className="prop-drawer-scrim"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) requestClose();
      }}
    >
      <form
        className="prop-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={mode === "create" ? "Add property" : `Edit ${title}`}
        onSubmit={handleSave}
        noValidate
      >
        <header className="prop-drawer__header">
          <div>
            <p className="prop-drawer__eyebrow">Property</p>
            <h2 className="prop-drawer__title">
              {mode === "create" ? "Add property" : title}
            </h2>
          </div>
          <button
            type="button"
            className="console-btn console-btn--ghost prop-drawer__close"
            aria-label="Close"
            onClick={requestClose}
          >
            <IconClose size={18} />
          </button>
        </header>

        {mode === "create" ? (
          // Create mode: the tab strip becomes a non-navigable step indicator.
          // Steps cannot be clicked directly — the footer Next/Back drive them
          // so Step-1 validation always runs before Step 2 is revealed.
          <ol className="prop-stepper" aria-label="Add property steps">
            <li
              className={
                step === 1
                  ? "prop-stepper__step prop-stepper__step--current"
                  : "prop-stepper__step prop-stepper__step--done"
              }
              aria-current={step === 1 ? "step" : undefined}
            >
              <span className="prop-stepper__circle" aria-hidden="true">
                {step > 1 ? <IconCheck size={15} /> : "1"}
              </span>
              <span className="prop-stepper__label">Property details</span>
            </li>
            <li
              className={
                step === 2
                  ? "prop-stepper__step prop-stepper__step--current"
                  : "prop-stepper__step prop-stepper__step--upcoming"
              }
              aria-current={step === 2 ? "step" : undefined}
            >
              <span className="prop-stepper__circle" aria-hidden="true">
                2
              </span>
              <span className="prop-stepper__label">Distribution settings</span>
            </li>
          </ol>
        ) : (
          <div className="prop-drawer__tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === "details"}
              className={
                tab === "details"
                  ? "prop-drawer__tab prop-drawer__tab--active"
                  : "prop-drawer__tab"
              }
              onClick={() => setTab("details")}
            >
              Property details
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "distribution"}
              className={
                tab === "distribution"
                  ? "prop-drawer__tab prop-drawer__tab--active"
                  : "prop-drawer__tab"
              }
              onClick={() => setTab("distribution")}
            >
              Distribution settings
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "danger"}
              className={
                tab === "danger"
                  ? "prop-drawer__tab prop-drawer__tab--active prop-drawer__tab--danger"
                  : "prop-drawer__tab prop-drawer__tab--danger"
              }
              onClick={() => setTab("danger")}
            >
              Danger zone
            </button>
          </div>
        )}

        <div className="prop-drawer__body">
          {loading ? (
            <p className="console-loading" role="status" aria-live="polite">
              Loading property…
            </p>
          ) : (
            <>
              {apiError !== null ? (
                <UserMessage message={apiError} severity="error" />
              ) : null}
              {fieldError !== null ? (
                <UserMessage
                  message={{
                    what: fieldError,
                    why: "This field is required.",
                    next: "Complete the highlighted field and try again.",
                  }}
                  severity="error"
                />
              ) : null}

              {/* Compact, truthful post-save result (edit mode; directive M). */}
              {mode === "edit" && saveResultState !== null && tab !== "danger"
                ? (() => {
                    const line = editSaveResultLine(saveResultState);
                    return (
                      <p
                        className={`prop-drawer__save-result prop-drawer__save-result--${line.tone}`}
                        role="status"
                        aria-live="polite"
                      >
                        {line.text}
                        {line.tone === "critical" ? (
                          <button
                            type="button"
                            className="console-btn console-btn--ghost prop-drawer__save-result-retry"
                            onClick={handleRetry}
                            disabled={retryBusy}
                          >
                            {retryBusy ? "Retrying…" : "Retry sync"}
                          </button>
                        ) : null}
                      </p>
                    );
                  })()
                : null}

              {tab === "details" ? (
                <div className="console-form">
                  {/* --- Basic information --- */}
                  <section className="prop-drawer__section">
                    <h3 className="prop-drawer__section-title">
                      Basic information
                    </h3>
                    <div className="console-form__row">
                      <div className="console-field">
                        <label htmlFor="pd-name">Property name</label>
                        <input
                          id="pd-name"
                          value={form.name}
                          onChange={(e) => patch({ name: e.target.value })}
                        />
                      </div>
                      <div className="console-field">
                        <label htmlFor="pd-currency">Default currency</label>
                        <select
                          id="pd-currency"
                          value={form.currency}
                          onChange={(e) => patch({ currency: e.target.value })}
                        >
                          {CURRENCIES.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="console-field">
                        <label htmlFor="pd-type">Property type</label>
                        <select
                          id="pd-type"
                          value={form.type}
                          onChange={(e) => patch({ type: e.target.value })}
                        >
                          {PROPERTY_TYPES.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="console-form__row">
                      {/* Billing type is derived by Channex from provider rules;
                          AVOS has no column for it, so it is a read-only,
                          non-persisted display field (never an invalid combo). */}
                      <div className="console-field console-field--full">
                        <label htmlFor="pd-billing">Billing type</label>
                        <input
                          id="pd-billing"
                          value="Derived from distribution"
                          disabled
                          readOnly
                        />
                        <p className="prop-help">
                          Derived from your distribution configuration.
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* --- Contact information --- */}
                  <section className="prop-drawer__section">
                    <h3 className="prop-drawer__section-title">
                      Contact information
                    </h3>
                    <div className="console-form__row">
                      <div className="console-field">
                        <label htmlFor="pd-email">Property email</label>
                        <input
                          id="pd-email"
                          type="email"
                          value={form.email}
                          onChange={(e) => patch({ email: e.target.value })}
                        />
                      </div>
                      <div className="console-field">
                        <label htmlFor="pd-phone">Phone</label>
                        <input
                          id="pd-phone"
                          value={form.phone}
                          onChange={(e) => patch({ phone: e.target.value })}
                        />
                      </div>
                      <div className="console-field">
                        <label htmlFor="pd-website">Website (optional)</label>
                        <input
                          id="pd-website"
                          type="url"
                          value={form.website}
                          onChange={(e) => patch({ website: e.target.value })}
                        />
                      </div>
                    </div>
                  </section>

                  {/* --- Location --- */}
                  <section className="prop-drawer__section">
                    <h3 className="prop-drawer__section-title">Location</h3>
                    <div className="console-form__row">
                      <div className="console-field">
                        <label htmlFor="pd-country">Country</label>
                        <select
                          id="pd-country"
                          value={form.country}
                          onChange={(e) => patch({ country: e.target.value })}
                        >
                          {COUNTRY_CODES.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="console-field">
                        <label htmlFor="pd-tz">Timezone</label>
                        <input
                          id="pd-tz"
                          value={form.timezone}
                          onChange={(e) => patch({ timezone: e.target.value })}
                        />
                      </div>
                      <div className="console-field">
                        <label htmlFor="pd-postcode">Postal code</label>
                        <input
                          id="pd-postcode"
                          value={form.postcode}
                          onChange={(e) => patch({ postcode: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="console-form__row">
                      <div className="console-field">
                        <label htmlFor="pd-state">State / Region</label>
                        <input
                          id="pd-state"
                          value={form.state}
                          onChange={(e) => patch({ state: e.target.value })}
                        />
                      </div>
                      <div className="console-field">
                        <label htmlFor="pd-city">City</label>
                        <input
                          id="pd-city"
                          value={form.city}
                          onChange={(e) => patch({ city: e.target.value })}
                        />
                      </div>
                      <div className="console-field">
                        <label htmlFor="pd-address">Address</label>
                        <input
                          id="pd-address"
                          value={form.address}
                          onChange={(e) => patch({ address: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="console-form__row">
                      <div className="console-field">
                        <button
                          type="button"
                          className="console-btn console-btn--ghost"
                          onClick={handleGeocode}
                          disabled={geoBusy}
                        >
                          {geoBusy ? "Finding…" : "Find location"}
                        </button>
                        {geoNote !== null ? (
                          <p className="prop-help">{geoNote}</p>
                        ) : null}
                      </div>
                      <div className="console-field">
                        <label htmlFor="pd-lat">Latitude</label>
                        <input
                          id="pd-lat"
                          type="number"
                          step="any"
                          value={form.latitude}
                          onChange={(e) => patch({ latitude: e.target.value })}
                        />
                      </div>
                      <div className="console-field">
                        <label htmlFor="pd-lon">Longitude</label>
                        <input
                          id="pd-lon"
                          type="number"
                          step="any"
                          value={form.longitude}
                          onChange={(e) => patch({ longitude: e.target.value })}
                        />
                      </div>
                    </div>
                    {geoError !== null ? (
                      <UserMessage message={geoError} severity="error" />
                    ) : null}
                  </section>

                  {/* --- Content (presentational; no backing columns yet) ---
                      These five fields mirror the Channex property content
                      model so the intended property shape is visible, but AVOS
                      has no content/media storage, so every control is disabled
                      and nothing here is persisted (absent from buildInput). */}
                  <section className="prop-drawer__section prop-content-section">
                    <h3 className="prop-drawer__section-title">Content</h3>
                    <div className="console-form__row">
                      <div className="console-field console-field--full">
                        <label htmlFor="pd-logo">Property logo</label>
                        <div
                          className="prop-content-upload"
                          aria-disabled="true"
                        >
                          <span className="prop-content-upload__glyph" aria-hidden="true">
                            <IconImage size={20} />
                          </span>
                          <button
                            type="button"
                            id="pd-logo"
                            className="console-btn console-btn--ghost"
                            disabled
                          >
                            Upload logo
                          </button>
                        </div>
                        <p className="prop-help">
                          Media upload will be available after AVOS media storage
                          is configured.
                        </p>
                      </div>
                    </div>
                    <div className="console-form__row">
                      <div className="console-field console-field--full">
                        <label htmlFor="pd-description">Description</label>
                        <textarea
                          id="pd-description"
                          rows={3}
                          placeholder="Property description"
                          disabled
                        />
                        <p className="prop-help">
                          Property content fields will be editable once AVOS
                          content storage is configured.
                        </p>
                      </div>
                    </div>
                    <div className="console-form__row">
                      <div className="console-field console-field--full">
                        <label htmlFor="pd-facilities">Facilities</label>
                        <textarea
                          id="pd-facilities"
                          rows={2}
                          placeholder="Facilities &amp; amenities"
                          disabled
                        />
                        <p className="prop-help">
                          Property content fields will be editable once AVOS
                          content storage is configured.
                        </p>
                      </div>
                    </div>
                    <div className="console-form__row">
                      <div className="console-field console-field--full">
                        <label htmlFor="pd-important">
                          Important information
                        </label>
                        <textarea
                          id="pd-important"
                          rows={2}
                          placeholder="Check-in instructions, house rules, etc."
                          disabled
                        />
                        <p className="prop-help">
                          Property content fields will be editable once AVOS
                          content storage is configured.
                        </p>
                      </div>
                    </div>
                    <div className="console-form__row">
                      <div className="console-field console-field--full">
                        <label htmlFor="pd-photos">Photos</label>
                        <div
                          className="prop-content-upload"
                          aria-disabled="true"
                        >
                          <span className="prop-content-upload__glyph" aria-hidden="true">
                            <IconImage size={20} />
                          </span>
                          <button
                            type="button"
                            id="pd-photos"
                            className="console-btn console-btn--ghost"
                            disabled
                          >
                            Upload photos
                          </button>
                        </div>
                        <p className="prop-help">
                          Media upload will be available after AVOS media storage
                          is configured.
                        </p>
                      </div>
                    </div>
                  </section>
                </div>
              ) : tab === "distribution" ? (
                <div className="console-form">
                  {/* --- Price limits --- */}
                  <section className="prop-drawer__section">
                    <h3 className="prop-drawer__section-title">Price limits</h3>
                    <div className="console-form__row">
                      <div className="console-field">
                        <label htmlFor="pd-minprice">Minimum price</label>
                        <input
                          id="pd-minprice"
                          inputMode="decimal"
                          value={form.minPrice}
                          onChange={(e) => patch({ minPrice: e.target.value })}
                        />
                      </div>
                      <div className="console-field">
                        <label htmlFor="pd-maxprice">Maximum price</label>
                        <input
                          id="pd-maxprice"
                          inputMode="decimal"
                          value={form.maxPrice}
                          onChange={(e) => patch({ maxPrice: e.target.value })}
                        />
                      </div>
                    </div>
                    <p className="prop-help">Leave blank for no limit.</p>
                  </section>

                  {/* --- Automatic inventory updates --- */}
                  <section className="prop-drawer__section">
                    <h3 className="prop-drawer__section-title">
                      Automatic inventory updates
                    </h3>
                    <div className="console-field console-field--checkbox">
                      <label htmlFor="pd-new-res">
                        <input
                          id="pd-new-res"
                          type="checkbox"
                          checked
                          disabled
                          readOnly
                        />
                        New reservation
                      </label>
                      <p className="prop-help">Required by Channex — always on.</p>
                    </div>
                    <div className="console-field console-field--checkbox">
                      <label htmlFor="pd-mod-res">
                        <input
                          id="pd-mod-res"
                          type="checkbox"
                          checked={form.autoupdateOnModification}
                          onChange={(e) =>
                            patch({ autoupdateOnModification: e.target.checked })
                          }
                        />
                        Modified reservation
                      </label>
                    </div>
                    <div className="console-field console-field--checkbox">
                      <label htmlFor="pd-cxl-res">
                        <input
                          id="pd-cxl-res"
                          type="checkbox"
                          checked={form.autoupdateOnCancellation}
                          onChange={(e) =>
                            patch({ autoupdateOnCancellation: e.target.checked })
                          }
                        />
                        Cancelled reservation
                      </label>
                    </div>
                  </section>

                  {/* --- Inventory window --- */}
                  <section className="prop-drawer__section">
                    <h3 className="prop-drawer__section-title">
                      Inventory window
                    </h3>
                    <div className="console-field">
                      <label htmlFor="pd-invdays">Inventory days</label>
                      <input
                        id="pd-invdays"
                        type="number"
                        min="100"
                        max="730"
                        value={form.inventoryDays}
                        onChange={(e) => patch({ inventoryDays: e.target.value })}
                      />
                      <p className="prop-help">
                        Availability is distributed this many days into the
                        future (100–730).
                      </p>
                    </div>
                  </section>

                  {/* --- Minimum stay --- */}
                  <section className="prop-drawer__section">
                    <h3 className="prop-drawer__section-title">Minimum stay</h3>
                    <div className="console-field">
                      <label htmlFor="pd-minstay">Minimum stay handling</label>
                      <select
                        id="pd-minstay"
                        value={form.minStayType}
                        onChange={(e) =>
                          patch({
                            minStayType: e.target.value as
                              | "both"
                              | "arrival"
                              | "through",
                          })
                        }
                      >
                        {MIN_STAY_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </section>

                  {/* --- Same-day booking cut-off --- */}
                  <section className="prop-drawer__section">
                    <h3 className="prop-drawer__section-title">
                      Same-day booking cut-off
                    </h3>
                    <div className="console-form__row">
                      <div className="console-field">
                        <label htmlFor="pd-cutoff-time">Cut-off time</label>
                        <input
                          id="pd-cutoff-time"
                          type="time"
                          value={form.cutOffTime}
                          onChange={(e) => patch({ cutOffTime: e.target.value })}
                        />
                      </div>
                      <div className="console-field">
                        <label htmlFor="pd-cutoff-days">Cut-off days</label>
                        <input
                          id="pd-cutoff-days"
                          type="number"
                          min="0"
                          value={form.cutOffDays}
                          onChange={(e) => patch({ cutOffDays: e.target.value })}
                        />
                      </div>
                    </div>
                    <p className="prop-help">
                      Availability closes at this time in the property’s
                      timezone, for today plus the cut-off days.
                    </p>
                  </section>

                  {/* --- Advance booking window --- */}
                  <section className="prop-drawer__section">
                    <h3 className="prop-drawer__section-title">
                      Advance booking window
                    </h3>
                    <div className="console-field console-field--checkbox">
                      <label htmlFor="pd-advance-enabled">
                        <input
                          id="pd-advance-enabled"
                          type="checkbox"
                          checked={form.advanceEnabled}
                          onChange={(e) =>
                            patch({ advanceEnabled: e.target.checked })
                          }
                        />
                        Enable maximum advance booking limit
                      </label>
                    </div>
                    {form.advanceEnabled ? (
                      <div className="console-field">
                        <label htmlFor="pd-advance-days">Advance days</label>
                        <input
                          id="pd-advance-days"
                          type="number"
                          min="1"
                          value={form.advanceDays}
                          onChange={(e) => patch({ advanceDays: e.target.value })}
                        />
                        <p className="prop-help">
                          Guests can book up to {Number(form.advanceDays) || 0}{" "}
                          days in advance.
                        </p>
                      </div>
                    ) : null}
                  </section>
                </div>
              ) : (
                <PropertyDangerZone
                  propertyId={propertyId}
                  propertyName={title}
                  onRemoved={onRemoved}
                />
              )}
            </>
          )}
        </div>

        <footer className="prop-drawer__footer">
          {mode === "create" ? (
            // Two-step wizard footer. Step 1: [Cancel] [Next →];
            // Step 2: [← Back] [Save as draft] [Create property].
            step === 1 ? (
              <>
                <button
                  type="button"
                  className="console-btn console-btn--ghost"
                  onClick={requestClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="console-btn"
                  onClick={handleNext}
                  disabled={busy || draftBusy || loading}
                >
                  Next →
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="console-btn console-btn--ghost prop-drawer__footer-spacer"
                  onClick={handleBack}
                  disabled={busy || draftBusy}
                >
                  ← Back
                </button>
                <button
                  type="button"
                  className="console-btn console-btn--ghost"
                  onClick={handleSaveDraft}
                  disabled={busy || draftBusy || loading}
                >
                  {draftBusy ? "Saving…" : "Save as draft"}
                </button>
                <button
                  type="submit"
                  className="console-btn"
                  disabled={busy || draftBusy || loading}
                >
                  {busy ? "Creating…" : "Create property"}
                </button>
              </>
            )
          ) : (
            <>
              {(distState === "error" || distState === "sync_failed") &&
              tab !== "danger" ? (
                <span className="prop-drawer__footer-spacer">
                  <button
                    type="button"
                    className="console-btn console-btn--ghost"
                    onClick={handleRetry}
                    disabled={retryBusy}
                  >
                    {retryBusy ? "Retrying…" : "Retry sync"}
                  </button>
                </span>
              ) : null}
              <button
                type="button"
                className="console-btn console-btn--ghost"
                onClick={requestClose}
              >
                {tab === "danger" ? "Close" : "Cancel"}
              </button>
              {tab !== "danger" ? (
                <button
                  type="submit"
                  className="console-btn"
                  disabled={busy || loading}
                >
                  {saveLabel}
                </button>
              ) : null}
            </>
          )}
        </footer>
      </form>
    </div>
  );
}

// ===========================================================================
// Danger Zone: Safe Property Removal / Decommissioning (Feature A).
//
// A visually-separated section inside the property drawer that loads the
// removal impact assessment, shows blockers (and disables removal when any
// exist), lists the ACTUAL connected channels in a dynamic disconnect warning,
// and requires a deliberate typed-name confirmation + understanding checkboxes
// before calling the backend. Backend-state-driven; SSR-safe (all network in
// effects/handlers). RBAC is enforced by the backend (403 for roles lacking
// properties.archive); the UI additionally hides the control for such roles.
// ===========================================================================

/**
 * A single actionable error message for every "couldn't load the assessment"
 * path (non-standard error bodies, malformed payloads, network failures).
 * Module-level so it is a stable reference across renders.
 */
const PROP_REMOVAL_LOAD_ERROR: UserMessageModel = {
  what: "Unable to load property removal details. Please try again.",
  why: "The removal impact could not be read from the server.",
  next: "Choose Try again. If this persists, contact support.",
};

/** Format a date string (ISO or YYYY-MM-DD) to a short, readable label. */
function formatRetirementDate(value: string | null): string {
  if (value === null || value.trim() === "") return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** A compact one-line summary for a future reservation row (CASE C list). */
function retirementReservationSummary(r: RetirementReservation): string {
  const parts: string[] = [];
  if (r.source.trim() !== "") parts.push(r.source);
  if (r.guestName !== null && r.guestName.trim() !== "") parts.push(r.guestName);
  const dates = `${formatRetirementDate(r.checkIn)} – ${formatRetirementDate(r.checkOut)}`;
  parts.push(dates);
  if (r.unitName !== null && r.unitName.trim() !== "") parts.push(r.unitName);
  return parts.join(" · ");
}

/**
 * The read-only "Resolve bookings now" sub-view (requirement 9/10/11/12).
 *
 * Direct future bookings can be cancelled from AVOS (single or bulk, behind a
 * dry-run preview confirmation); OTA bookings are NEVER bulk-cancellable here —
 * they carry read-only guidance to resolve at the booking source. On any
 * cancellation the parent re-loads the assessment so the blocker shrinks.
 */
function ResolveBookingsNow({
  propertyId,
  reservations,
  directCancellableCount,
  otaCount,
  canRemove,
  onResolved,
  onBack,
}: {
  readonly propertyId: string;
  readonly reservations: readonly RetirementReservation[];
  readonly directCancellableCount: number;
  readonly otaCount: number;
  readonly canRemove: boolean;
  readonly onResolved: (cancelledCount: number) => void;
  readonly onBack: () => void;
}): ReactElement {
  const [busy, setBusy] = useState(false);
  const [apiError, setApiError] = useState<UserMessageModel | null>(null);
  // The bulk preview count ("N direct bookings will be cancelled"); null until
  // the operator asks to preview the bulk cancellation.
  const [bulkPreview, setBulkPreview] = useState<number | null>(null);

  const directRes = reservations.filter((r) => r.isDirect);
  const otaRes = reservations.filter((r) => !r.isDirect);

  async function cancelOne(reservationId: string): Promise<void> {
    setApiError(null);
    setBusy(true);
    try {
      const res = await apiResolveDirectBookings({ propertyId, reservationIds: [reservationId] });
      const err = asError(res);
      if (err !== null) {
        setApiError(toUserMessage(err));
        return;
      }
      const resolved = (res as { resolved?: { cancelledCount?: number } }).resolved;
      onResolved(resolved?.cancelledCount ?? 0);
    } catch {
      setApiError({
        what: "The booking could not be cancelled.",
        why: "The cancellation request failed to complete.",
        next: "Try again. If this persists, contact support.",
      });
    } finally {
      setBusy(false);
    }
  }

  async function previewBulk(): Promise<void> {
    setApiError(null);
    setBusy(true);
    try {
      const res = await apiResolveDirectBookings({ propertyId, preview: true });
      const err = asError(res);
      if (err !== null) {
        setApiError(toUserMessage(err));
        return;
      }
      const preview = (res as { preview?: { eligibleDirectCount?: number } }).preview;
      setBulkPreview(preview?.eligibleDirectCount ?? 0);
    } catch {
      setApiError({
        what: "The cancellation preview could not be loaded.",
        why: "The preview request failed to complete.",
        next: "Try again. If this persists, contact support.",
      });
    } finally {
      setBusy(false);
    }
  }

  async function confirmBulk(): Promise<void> {
    setApiError(null);
    setBusy(true);
    try {
      // Omit reservationIds → cancel ALL eligible direct future bookings.
      const res = await apiResolveDirectBookings({ propertyId });
      const err = asError(res);
      if (err !== null) {
        setApiError(toUserMessage(err));
        return;
      }
      const resolved = (res as { resolved?: { cancelledCount?: number } }).resolved;
      setBulkPreview(null);
      onResolved(resolved?.cancelledCount ?? 0);
    } catch {
      setApiError({
        what: "The bookings could not be cancelled.",
        why: "The bulk cancellation request failed to complete.",
        next: "Try again. If this persists, contact support.",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="prop-danger__resolve">
      <div className="prop-danger__resolve-head">
        <h4 className="prop-danger__subtitle">Resolve bookings now</h4>
        <button type="button" className="console-btn console-btn--ghost" onClick={onBack}>
          Back
        </button>
      </div>

      {apiError !== null ? <UserMessage message={apiError} severity="error" /> : null}

      {/* ---- Direct bookings (cancellable from AVOS) ---- */}
      <section className="prop-danger__resolve-group">
        <h5 className="prop-danger__group-title">
          Direct bookings{directCancellableCount > 0 ? ` (${directCancellableCount})` : ""}
        </h5>
        {directRes.length === 0 ? (
          <p className="prop-help">No direct bookings to resolve.</p>
        ) : (
          <>
            <ul className="prop-danger__res-list">
              {directRes.map((r) => (
                <li key={r.reservationId} className="prop-danger__res-row">
                  <span className="prop-danger__res-text">
                    {retirementReservationSummary(r)}
                    {r.reference !== null && r.reference.trim() !== "" ? (
                      <span className="prop-danger__res-ref"> · {r.reference}</span>
                    ) : null}
                    <span className="prop-chip prop-danger__res-status">{r.status}</span>
                  </span>
                  {canRemove ? (
                    <button
                      type="button"
                      className="console-btn console-btn--ghost"
                      disabled={busy}
                      onClick={() => void cancelOne(r.reservationId)}
                    >
                      Cancel booking
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>

            {canRemove && directCancellableCount > 0 ? (
              bulkPreview === null ? (
                <button
                  type="button"
                  className="console-btn console-btn--danger"
                  disabled={busy}
                  onClick={() => void previewBulk()}
                >
                  {busy
                    ? "Working…"
                    : `Cancel ${directCancellableCount} eligible direct booking${
                        directCancellableCount === 1 ? "" : "s"
                      }`}
                </button>
              ) : (
                <div className="prop-danger__confirm-bulk">
                  <p className="prop-danger__confirm-text">
                    {bulkPreview} direct booking{bulkPreview === 1 ? "" : "s"} will be cancelled.
                  </p>
                  <div className="prop-danger__btn-row">
                    <button
                      type="button"
                      className="console-btn console-btn--danger"
                      disabled={busy}
                      onClick={() => void confirmBulk()}
                    >
                      {busy ? "Cancelling…" : "Confirm cancellation"}
                    </button>
                    <button
                      type="button"
                      className="console-btn console-btn--ghost"
                      disabled={busy}
                      onClick={() => setBulkPreview(null)}
                    >
                      Keep bookings
                    </button>
                  </div>
                </div>
              )
            ) : null}
          </>
        )}
      </section>

      {/* ---- OTA bookings (resolve at the channel; never bulk-cancelled) ---- */}
      <section className="prop-danger__resolve-group">
        <h5 className="prop-danger__group-title">
          OTA bookings{otaCount > 0 ? ` (${otaCount})` : ""}
        </h5>
        {otaRes.length === 0 ? (
          <p className="prop-help">No OTA bookings to resolve.</p>
        ) : (
          <>
            <p className="prop-help prop-danger__ota-guidance">
              {otaCount} OTA reservation{otaCount === 1 ? "" : "s"} require resolution at their
              booking source. Cancel or modify these in the channel (e.g. Booking.com); AVOS updates
              automatically when the channel sends the change.
            </p>
            <ul className="prop-danger__res-list">
              {otaRes.map((r) => (
                <li key={r.reservationId} className="prop-danger__res-row prop-danger__res-row--ota">
                  <span className="prop-danger__res-text">
                    {retirementReservationSummary(r)}
                    {r.reference !== null && r.reference.trim() !== "" ? (
                      <span className="prop-danger__res-ref"> · {r.reference}</span>
                    ) : null}
                    <span className="prop-chip prop-danger__res-status">{r.status}</span>
                  </span>
                  <span className="prop-help prop-danger__res-channel-note">
                    Manage at {r.source.trim() !== "" ? r.source : "channel"}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </div>
  );
}

/** The within-Danger-Zone retirement sub-view selector for CASE C. */
type RetirementStep = "options" | "schedule" | "resolve" | "support";

export function PropertyDangerZone({
  propertyId,
  propertyName,
  onRemoved,
}: {
  readonly propertyId: string | null;
  readonly propertyName: string;
  readonly onRemoved: (
    outcome: "deleted" | "decommissioned",
    name: string,
    manualCleanupNote?: string | null,
  ) => void;
}): ReactElement {
  const me = useMe();
  const canRemove = me.can("properties.archive");
  const [loading, setLoading] = useState(true);
  const [assessment, setAssessment] = useState<RetirementAssessment | null>(null);
  const [loadError, setLoadError] = useState<UserMessageModel | null>(null);
  const [confirmName, setConfirmName] = useState("");
  const [ackChannels, setAckChannels] = useState(false);
  const [ackDecommission, setAckDecommission] = useState(false);
  // CASE C schedule: stop-new-bookings defaults ON (recommended); the retention
  // acknowledgement must be ticked before scheduling.
  const [stopNewBookings, setStopNewBookings] = useState(true);
  const [ackRetention, setAckRetention] = useState(false);
  const [busy, setBusy] = useState(false);
  const [apiError, setApiError] = useState<UserMessageModel | null>(null);
  const [step, setStep] = useState<RetirementStep>("options");
  // Bumping this re-triggers the load effect (used by the "Try again" button
  // and after a resolve/schedule/cancel so the assessment refreshes in place).
  const [reloadTick, setReloadTick] = useState(0);

  // Load the retirement assessment (network in an effect → SSR-safe). Hardened
  // to treat ANY non-well-formed response (bare-string errors, 403/404/500
  // bodies that are not the structured envelope, partial/malformed payloads,
  // network throws) as an inline, retryable error instead of letting an
  // undefined assessment slip through to the success render and throw.
  useEffect(() => {
    if (propertyId === null || !canRemove) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setLoadError(null);
    apiRetirementAssessment(propertyId)
      .then((res) => {
        if (!active) return;
        // Structured error envelope → surface it directly.
        const err = asError(res);
        if (err !== null) {
          setAssessment(null);
          setLoadError(toUserMessage(err));
          setLoading(false);
          return;
        }
        // Treat the result as valid ONLY when it is a well-formed assessment
        // object with the required shape. This catches the dev-server
        // bare-string 404 (`{"error":"not found"}`), any non-envelope error
        // body, and partial/malformed payloads — none of which asError sees.
        const candidate = (res as { assessment?: unknown } | null | undefined)?.assessment;
        if (
          candidate === null ||
          candidate === undefined ||
          typeof candidate !== "object" ||
          typeof (candidate as { retirementCase?: unknown }).retirementCase !== "string" ||
          !Array.isArray((candidate as { futureReservations?: unknown }).futureReservations) ||
          typeof (candidate as { removal?: unknown }).removal !== "object" ||
          (candidate as { removal?: unknown }).removal === null ||
          !Array.isArray(
            ((candidate as { removal?: { blockers?: unknown } }).removal ?? {}).blockers,
          )
        ) {
          setAssessment(null);
          setLoadError(PROP_REMOVAL_LOAD_ERROR);
          setLoading(false);
          return;
        }
        setAssessment(candidate as RetirementAssessment);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        // A network/parse throw must also land on the actionable inline error
        // (not the silent "unavailable" fallback) so the user gets Try again.
        setAssessment(null);
        setLoadError(PROP_REMOVAL_LOAD_ERROR);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [propertyId, canRemove, reloadTick]);

  function reload(): void {
    setStep("options");
    setReloadTick((t) => t + 1);
  }

  if (!canRemove) {
    return (
      <div className="console-form prop-danger">
        <UserMessage
          message={{
            what: "You don't have permission to remove this property.",
            why: 'Removing a property requires the "properties.archive" permission.',
            next: "Ask an Owner to remove the property if it is no longer needed.",
          }}
          severity="status"
        />
      </div>
    );
  }

  if (loading) {
    return (
      <p className="console-loading" role="status" aria-live="polite">
        Checking what removing this property affects…
      </p>
    );
  }

  if (loadError !== null) {
    return (
      <div className="console-form prop-danger">
        <UserMessage message={loadError} severity="error" />
        <button
          type="button"
          className="console-btn"
          onClick={() => setReloadTick((t) => t + 1)}
        >
          Try again
        </button>
      </div>
    );
  }

  if (assessment === null) {
    return (
      <div className="console-form prop-danger">
        <UserMessage
          message={{
            what: "Removal details are unavailable.",
            why: "The removal impact could not be loaded.",
            next: "Close and reopen this panel to try again.",
          }}
          severity="error"
        />
      </div>
    );
  }

  // Defence-in-depth: read every field through a safe local so a future/partial
  // payload can NEVER throw and blank the app.
  const removal = assessment.removal;
  const connections = removal.activeConnections ?? [];
  const liveChannels = connections.filter((c) => c.live);
  const hasChannels = liveChannels.length > 0;
  const retirementCase = assessment.retirementCase;
  const futureReservations = assessment.futureReservations ?? [];
  const directCancellableCount = assessment.directCancellableCount ?? 0;
  const otaCount = assessment.otaCount ?? 0;
  const isScheduled = assessment.retirementState === "scheduled";
  const nameMatches = confirmName.trim() === propertyName.trim();

  // --- Shared removal (CASE A hard delete / CASE B decommission) ---
  async function handleRemove(mode: RemovalMode): Promise<void> {
    if (propertyId === null) return;
    setApiError(null);
    setBusy(true);
    try {
      const res = await apiRemoveProperty({
        propertyId,
        mode,
        confirmName: confirmName.trim(),
      });
      const err = asError(res);
      if (err !== null) {
        setApiError(toUserMessage(err));
        return;
      }
      const result = (res as {
        removal: {
          outcome: "deleted" | "decommissioned";
          manualCleanupRequired?: boolean;
          extranetDisconnectRequired?: boolean;
          manualCleanupMessage?: string | null;
        };
      }).removal;
      const manualCleanupNote =
        (result.manualCleanupRequired === true || result.extranetDisconnectRequired === true) &&
        typeof result.manualCleanupMessage === "string" &&
        result.manualCleanupMessage.trim() !== ""
          ? result.manualCleanupMessage
          : null;
      onRemoved(result.outcome, propertyName, manualCleanupNote);
    } catch {
      setApiError({
        what: "The property could not be removed.",
        why: "The removal request failed to complete.",
        next: "Try again. If this persists, contact support.",
      });
    } finally {
      setBusy(false);
    }
  }

  // --- CASE C: schedule a decommission after the final checkout ---
  async function handleSchedule(): Promise<void> {
    if (propertyId === null) return;
    setApiError(null);
    setBusy(true);
    try {
      const res = await apiScheduleDecommission({
        propertyId,
        confirmName: confirmName.trim(),
        stopNewBookings,
      });
      const err = asError(res);
      if (err !== null) {
        setApiError(toUserMessage(err));
        return;
      }
      // Scheduled → surface via onRemoved as a decommission with a note so the
      // parent closes the drawer, shows a status, and refreshes the list.
      onRemoved(
        "decommissioned",
        propertyName,
        "This property is now scheduled for decommission after its final checkout. It remains available for existing reservations until then.",
      );
    } catch {
      setApiError({
        what: "The decommission could not be scheduled.",
        why: "The schedule request failed to complete.",
        next: "Try again. If this persists, contact support.",
      });
    } finally {
      setBusy(false);
    }
  }

  // --- CASE C scheduled-state: cancel the scheduled decommission ---
  async function handleCancelSchedule(): Promise<void> {
    if (propertyId === null) return;
    setApiError(null);
    setBusy(true);
    try {
      const res = await apiCancelScheduledDecommission(propertyId);
      const err = asError(res);
      if (err !== null) {
        setApiError(toUserMessage(err));
        return;
      }
      onRemoved(
        "decommissioned",
        propertyName,
        "Scheduled decommission cancelled; the property is active again.",
      );
    } catch {
      setApiError({
        what: "The scheduled decommission could not be cancelled.",
        why: "The request failed to complete.",
        next: "Try again. If this persists, contact support.",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="console-form prop-danger">
      <section className="prop-drawer__section prop-danger__box">
        <h3 className="prop-drawer__section-title prop-danger__title">
          Danger zone — Remove property
        </h3>

        {/* ---------------------------------------------------------------
            Already-scheduled state (requirement 24): show the schedule + a
            RBAC-gated Cancel action instead of the decision tree.
        ---------------------------------------------------------------- */}
        {isScheduled ? (
          <div className="prop-danger__scheduled">
            <UserMessage
              message={{
                what: `Decommission scheduled for after ${formatRetirementDate(
                  assessment.scheduledDecommissionAt,
                )}.`,
                why: `Stop new bookings: ${assessment.stopNewBookings ? "on" : "off"}. The property stays available in AVOS for existing reservations until its final checkout.`,
                next: "You can cancel the scheduled decommission to keep the property active.",
              }}
              severity="status"
            />
            {apiError !== null ? <UserMessage message={apiError} severity="error" /> : null}
            <button
              type="button"
              className="console-btn console-btn--danger"
              disabled={busy}
              onClick={() => void handleCancelSchedule()}
            >
              {busy ? "Cancelling…" : "Cancel scheduled decommission"}
            </button>
          </div>
        ) : retirementCase === "canHardDelete" ? (
          /* -------------------------------------------------------------
             CASE A — hard delete (no bookings / no history).
          ------------------------------------------------------------- */
          <div className="prop-danger__case">
            <p className="prop-help">
              This property can be removed now. It has no bookings or history.
            </p>
            {hasChannels ? (
              <div className="prop-danger__channels">
                <UserMessage
                  message={{
                    what: "AVOS will remove the channel connection from Channex and stop synchronisation.",
                    why: `This affects: ${liveChannels.map((c) => c.otaCode).join(", ")}.`,
                    next: "Confirm below to proceed.",
                  }}
                  severity="status"
                />
                <label className="prop-danger__check">
                  <input
                    type="checkbox"
                    checked={ackChannels}
                    onChange={(e) => setAckChannels(e.target.checked)}
                  />
                  I understand connected channels will be disconnected.
                </label>
              </div>
            ) : null}
            <div className="console-field prop-danger__confirm">
              <label htmlFor="pd-remove-confirm">
                Type the property name <strong>{propertyName}</strong> to confirm
              </label>
              <input
                id="pd-remove-confirm"
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                placeholder={propertyName}
                autoComplete="off"
              />
            </div>
            {apiError !== null ? <UserMessage message={apiError} severity="error" /> : null}
            <button
              type="button"
              className="console-btn console-btn--danger"
              disabled={!nameMatches || (hasChannels && !ackChannels) || busy}
              onClick={() => void handleRemove("hard_delete")}
            >
              {busy ? "Removing…" : "Delete property permanently"}
            </button>
          </div>
        ) : retirementCase === "canDecommissionNow" ? (
          /* -------------------------------------------------------------
             CASE B — decommission now (history, but no future bookings).
          ------------------------------------------------------------- */
          <div className="prop-danger__case">
            <p className="prop-help">
              This property can be decommissioned now. Historical bookings, guests, revenue and
              audit records will be retained.
            </p>
            {hasChannels ? (
              <div className="prop-danger__channels">
                <UserMessage
                  message={{
                    what: "AVOS will remove the channel connection from Channex and stop synchronisation.",
                    why: `This affects: ${liveChannels.map((c) => c.otaCode).join(", ")}.`,
                    next: "Confirm below to proceed.",
                  }}
                  severity="status"
                />
                <label className="prop-danger__check">
                  <input
                    type="checkbox"
                    checked={ackChannels}
                    onChange={(e) => setAckChannels(e.target.checked)}
                  />
                  I understand connected channels will be disconnected.
                </label>
              </div>
            ) : null}
            <label className="prop-danger__check">
              <input
                type="checkbox"
                checked={ackDecommission}
                onChange={(e) => setAckDecommission(e.target.checked)}
              />
              I understand this property will be decommissioned (archived) and its historical data
              retained, not permanently deleted.
            </label>
            <div className="console-field prop-danger__confirm">
              <label htmlFor="pd-remove-confirm">
                Type the property name <strong>{propertyName}</strong> to confirm
              </label>
              <input
                id="pd-remove-confirm"
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                placeholder={propertyName}
                autoComplete="off"
              />
            </div>
            {apiError !== null ? <UserMessage message={apiError} severity="error" /> : null}
            <button
              type="button"
              className="console-btn console-btn--danger"
              disabled={!nameMatches || !ackDecommission || (hasChannels && !ackChannels) || busy}
              onClick={() => void handleRemove("decommission")}
            >
              {busy ? "Decommissioning…" : "Decommission property"}
            </button>
          </div>
        ) : (
          /* -------------------------------------------------------------
             CASE C — requires retirement (future reservations block removal).
          ------------------------------------------------------------- */
          <div className="prop-danger__case prop-danger__retirement">
            <p className="prop-help prop-danger__retire-headline">
              This property has {futureReservations.length} future reservation
              {futureReservations.length === 1 ? "" : "s"} and cannot be removed immediately because
              these bookings still need to be serviced.
            </p>

            {/* The future reservations list. */}
            {futureReservations.length > 0 ? (
              <ul className="prop-danger__res-list prop-danger__future-list">
                {futureReservations.map((r) => (
                  <li key={r.reservationId} className="prop-danger__res-row">
                    <span className="prop-danger__res-text">
                      {retirementReservationSummary(r)}
                      {r.reference !== null && r.reference.trim() !== "" ? (
                        <span className="prop-danger__res-ref"> · {r.reference}</span>
                      ) : null}
                      <span className="prop-chip prop-danger__res-status">{r.status}</span>
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}

            {step === "resolve" ? (
              <ResolveBookingsNow
                propertyId={propertyId as string}
                reservations={futureReservations}
                directCancellableCount={directCancellableCount}
                otaCount={otaCount}
                canRemove={canRemove}
                onResolved={() => reload()}
                onBack={() => setStep("options")}
              />
            ) : step === "support" ? (
              <div className="prop-danger__support">
                <h4 className="prop-danger__subtitle">Contact AVOS support</h4>
                <p className="prop-help">
                  If you need help retiring this property while reservations remain, AVOS support can
                  guide you through the options. This does not bypass the safety checks — existing
                  bookings must still be serviced or resolved first.
                </p>
                <p className="prop-help">
                  Reach us at <strong>support@avos.app</strong> with your property name and the
                  reservations you need to resolve.
                </p>
                <button
                  type="button"
                  className="console-btn console-btn--ghost"
                  onClick={() => setStep("options")}
                >
                  Back
                </button>
              </div>
            ) : step === "schedule" ? (
              <div className="prop-danger__schedule">
                <h4 className="prop-danger__subtitle">
                  Schedule decommission after final checkout
                </h4>
                <p className="prop-help">
                  Last checkout: {formatRetirementDate(assessment.lastCheckoutDate)}
                </p>
                <label className="prop-danger__check">
                  <input
                    type="checkbox"
                    checked={stopNewBookings}
                    onChange={(e) => setStopNewBookings(e.target.checked)}
                  />
                  Stop accepting new bookings
                </label>
                <label className="prop-danger__check">
                  <input
                    type="checkbox"
                    checked={ackRetention}
                    onChange={(e) => setAckRetention(e.target.checked)}
                  />
                  I understand this property will remain available in AVOS for existing reservations
                  and will be decommissioned after the final checkout.
                </label>
                <div className="console-field prop-danger__confirm">
                  <label htmlFor="pd-schedule-confirm">
                    Type the property name <strong>{propertyName}</strong> to confirm
                  </label>
                  <input
                    id="pd-schedule-confirm"
                    value={confirmName}
                    onChange={(e) => setConfirmName(e.target.value)}
                    placeholder={propertyName}
                    autoComplete="off"
                  />
                </div>
                {apiError !== null ? <UserMessage message={apiError} severity="error" /> : null}
                <div className="prop-danger__btn-row">
                  <button
                    type="button"
                    className="console-btn console-btn--danger"
                    disabled={!nameMatches || !ackRetention || busy}
                    onClick={() => void handleSchedule()}
                  >
                    {busy ? "Scheduling…" : "Schedule decommission"}
                  </button>
                  <button
                    type="button"
                    className="console-btn console-btn--ghost"
                    disabled={busy}
                    onClick={() => setStep("options")}
                  >
                    Back
                  </button>
                </div>
              </div>
            ) : (
              /* The three CASE-C options. */
              <div className="prop-danger__options">
                <div className="prop-danger__option prop-danger__option--recommended">
                  <h4 className="prop-danger__subtitle">
                    Schedule decommission after final checkout
                    <span className="prop-chip prop-danger__recommended">Recommended</span>
                  </h4>
                  <p className="prop-help">
                    Last checkout: {formatRetirementDate(assessment.lastCheckoutDate)}
                  </p>
                  <button
                    type="button"
                    className="console-btn"
                    onClick={() => {
                      setApiError(null);
                      setStep("schedule");
                    }}
                  >
                    Schedule decommission
                  </button>
                </div>

                <div className="prop-danger__option">
                  <h4 className="prop-danger__subtitle">Resolve bookings now</h4>
                  <p className="prop-help">
                    Cancel eligible direct bookings in AVOS and resolve OTA bookings at their source,
                    then remove the property once no future reservations remain.
                  </p>
                  <button
                    type="button"
                    className="console-btn"
                    onClick={() => {
                      setApiError(null);
                      setStep("resolve");
                    }}
                  >
                    Resolve bookings now
                  </button>
                </div>

                <div className="prop-danger__option">
                  <h4 className="prop-danger__subtitle">Contact AVOS support</h4>
                  <p className="prop-help">
                    Need help? AVOS support can guide you through retiring this property safely.
                  </p>
                  <button
                    type="button"
                    className="console-btn console-btn--ghost"
                    onClick={() => setStep("support")}
                  >
                    Contact AVOS support
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

// ===========================================================================
// Unit inventory: shared helpers (accommodation types, sync pills, occupancy)
// ===========================================================================

/** Accommodation types (label → wire value). "Dorm" also sets roomKind='dorm'. */
const ACCOMMODATION_TYPES: readonly { readonly value: string; readonly label: string }[] = [
  { value: "room", label: "Room" },
  { value: "suite", label: "Suite" },
  { value: "cottage", label: "Cottage" },
  { value: "villa", label: "Villa" },
  { value: "apartment", label: "Apartment" },
  { value: "dorm", label: "Dorm" },
  { value: "other", label: "Other" },
];

/** Title-case an accommodation type value for display in the table. */
function accommodationLabel(type: string | null): string {
  if (type === null || type.trim() === "") return "—";
  const match = ACCOMMODATION_TYPES.find((t) => t.value === type.toLowerCase());
  if (match !== undefined) return match.label;
  return type.charAt(0).toUpperCase() + type.slice(1);
}

/** The display sync status for a unit, derived from its Channex mapping. */
type UnitSyncDisplay =
  | "synced"
  | "not_connected"
  | "pending"
  | "syncing"
  | "needs_attention";

/** Cached per-unit Channex mapping + derived display status. */
interface UnitSyncInfo {
  readonly channexRoomTypeId: string | null;
  readonly status: UnitSyncDisplay;
}

/** Map a display sync status to a pill class + label (§17). */
function syncPill(status: UnitSyncDisplay): {
  readonly cls: string;
  readonly label: string;
} {
  switch (status) {
    case "synced":
      return { cls: "avos-status avos-status--ok", label: "Synced" };
    case "pending":
      return { cls: "avos-status avos-status--pending", label: "Pending" };
    case "syncing":
      return { cls: "avos-status avos-status--pending", label: "Syncing" };
    case "needs_attention":
      return {
        cls: "avos-status avos-status--attention",
        label: "Needs attention",
      };
    case "not_connected":
    default:
      return { cls: "avos-status", label: "Not connected" };
  }
}

/** Guests summary for the Occupancy column. */
function occupancySummary(u: Unit): string {
  const guests = u.maxAdults ?? u.maxOccupancy;
  return `${guests} guest${guests === 1 ? "" : "s"}`;
}

/** Inventory summary for the Inventory column. */
function inventorySummary(u: Unit): string {
  const rooms = u.countOfRooms ?? 1;
  return `${rooms} room${rooms === 1 ? "" : "s"}`;
}

// ===========================================================================
// Unit form: the editable field set shared by the Add + Manage→Overview drawers
// ===========================================================================

/** Editable form state held by the unit drawer while open. */
interface UnitFormState {
  name: string;
  type: string;
  countOfRooms: string;
  status: "active" | "archived";
  maxAdults: string;
  maxChildren: string;
  maxInfants: string;
  defaultOccupancy: string;
  amenities: string;
  createRatePlan: boolean;
  baseRate: string;
  currency: string;
  ratePlanName: string;
}

/** The initial (create-mode) unit form state. Pure — no Date/window use. */
function emptyUnitForm(): UnitFormState {
  return {
    name: "",
    type: "room",
    countOfRooms: "1",
    status: "active",
    maxAdults: "2",
    maxChildren: "0",
    maxInfants: "0",
    defaultOccupancy: "2",
    amenities: "",
    createRatePlan: true,
    baseRate: "",
    currency: "INR",
    ratePlanName: "Standard",
  };
}

/** Map an existing unit into unit form state (edit-mode prefill). */
function unitFormFromUnit(u: Unit): UnitFormState {
  return {
    name: u.name,
    type: (u.type ?? "room").toLowerCase(),
    countOfRooms: String(u.countOfRooms ?? 1),
    status: u.status === "archived" ? "archived" : "active",
    maxAdults: String(u.maxAdults ?? u.maxOccupancy),
    maxChildren: String(u.maxChildren ?? 0),
    maxInfants: String(u.maxInfants ?? 0),
    defaultOccupancy: String(u.defaultOccupancy ?? u.maxOccupancy),
    amenities: u.amenities.join(", "),
    // Editing never re-creates a rate plan — that lives on the Rate Plans tab.
    createRatePlan: false,
    baseRate:
      u.baseRateMinor > 0 ? (u.baseRateMinor / 100).toString() : "",
    currency: u.baseRateCurrency,
    ratePlanName: "Standard",
  };
}

/** Validate unit form state (§8). Returns an inline message or null. */
function validateUnitForm(form: UnitFormState): string | null {
  if (form.name.trim() === "") return "Unit name is required.";
  const rooms = Number(form.countOfRooms);
  if (!Number.isFinite(rooms) || rooms < 1)
    return "Count of rooms must be at least 1.";
  const adults = Number(form.maxAdults);
  if (!Number.isFinite(adults) || adults < 1)
    return "Maximum adults must be at least 1.";
  const children = Number(form.maxChildren);
  if (!Number.isFinite(children) || children < 0)
    return "Child-only beds cannot be negative.";
  const infants = Number(form.maxInfants);
  if (!Number.isFinite(infants) || infants < 0)
    return "Infant cots cannot be negative.";
  const defaultOcc = Number(form.defaultOccupancy);
  if (!Number.isFinite(defaultOcc) || defaultOcc < 1)
    return "Default occupancy must be at least 1.";
  if (defaultOcc > adults)
    return "Default occupancy cannot exceed maximum adults.";
  if (form.createRatePlan && form.baseRate.trim() !== "") {
    const rate = parseMoneyToMinor(form.baseRate);
    if (rate === null || rate < 0)
      return "Base rate must be a non-negative amount.";
  }
  return null;
}

/** The editable unit field set. Reused by both the Add + Manage drawers. */
function UnitFormFields({
  form,
  patch,
  idPrefix,
}: {
  readonly form: UnitFormState;
  readonly patch: (next: Partial<UnitFormState>) => void;
  readonly idPrefix: string;
}): ReactElement {
  return (
    <div className="console-form">
      {/* --- Unit details --- */}
      <section className="prop-drawer__section">
        <h3 className="console-form__title">Unit details</h3>
        <div className="console-form__row">
          <div className="console-field">
            <label htmlFor={`${idPrefix}-name`}>Unit Name*</label>
            <input
              id={`${idPrefix}-name`}
              value={form.name}
              onChange={(e) => patch({ name: e.target.value })}
            />
          </div>
          <div className="console-field">
            <label htmlFor={`${idPrefix}-type`}>Accommodation Type*</label>
            <select
              id={`${idPrefix}-type`}
              value={form.type}
              onChange={(e) => patch({ type: e.target.value })}
            >
              {ACCOMMODATION_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="console-form__row">
          <div className="console-field">
            <label htmlFor={`${idPrefix}-rooms`}>Count of Rooms*</label>
            <input
              id={`${idPrefix}-rooms`}
              type="number"
              min="1"
              value={form.countOfRooms}
              onChange={(e) => patch({ countOfRooms: e.target.value })}
            />
          </div>
          <div className="console-field">
            <label htmlFor={`${idPrefix}-status`}>Status</label>
            <select
              id={`${idPrefix}-status`}
              value={form.status}
              onChange={(e) =>
                patch({
                  status: e.target.value === "archived" ? "archived" : "active",
                })
              }
            >
              <option value="active">Active</option>
              <option value="archived">Inactive</option>
            </select>
          </div>
        </div>
      </section>

      {/* --- Occupancy --- */}
      <section className="prop-drawer__section">
        <h3 className="console-form__title">Occupancy</h3>
        <div className="console-form__row">
          <div className="console-field">
            <label htmlFor={`${idPrefix}-adults`}>Maximum Adults*</label>
            <input
              id={`${idPrefix}-adults`}
              type="number"
              min="1"
              value={form.maxAdults}
              onChange={(e) => patch({ maxAdults: e.target.value })}
            />
          </div>
          <div className="console-field">
            <label htmlFor={`${idPrefix}-children`}>Child-only Beds</label>
            <input
              id={`${idPrefix}-children`}
              type="number"
              min="0"
              value={form.maxChildren}
              onChange={(e) => patch({ maxChildren: e.target.value })}
            />
          </div>
        </div>
        <div className="console-form__row">
          <div className="console-field">
            <label htmlFor={`${idPrefix}-infants`}>Infant Cots</label>
            <input
              id={`${idPrefix}-infants`}
              type="number"
              min="0"
              value={form.maxInfants}
              onChange={(e) => patch({ maxInfants: e.target.value })}
            />
          </div>
          <div className="console-field">
            <label htmlFor={`${idPrefix}-defocc`}>Default Occupancy*</label>
            <input
              id={`${idPrefix}-defocc`}
              type="number"
              min="1"
              value={form.defaultOccupancy}
              onChange={(e) => patch({ defaultOccupancy: e.target.value })}
            />
          </div>
        </div>
      </section>

      {/* --- Amenities --- */}
      <section className="prop-drawer__section">
        <h3 className="console-form__title">Amenities</h3>
        <div className="console-field">
          <label htmlFor={`${idPrefix}-amenities`}>
            Amenities (comma-separated)
          </label>
          <input
            id={`${idPrefix}-amenities`}
            placeholder="wifi, pool, breakfast"
            value={form.amenities}
            onChange={(e) => patch({ amenities: e.target.value })}
          />
        </div>
      </section>

      {/* --- Optional initial rate (create-mode surface uses this toggle) --- */}
      <section className="prop-drawer__section">
        <h3 className="console-form__title">Initial rate plan</h3>
        <div className="console-field console-field--checkbox">
          <label htmlFor={`${idPrefix}-createrp`}>
            <input
              id={`${idPrefix}-createrp`}
              type="checkbox"
              checked={form.createRatePlan}
              onChange={(e) => patch({ createRatePlan: e.target.checked })}
            />
            Create Standard Rate Plan
          </label>
        </div>
        {form.createRatePlan ? (
          <div className="console-form__row">
            <div className="console-field">
              <label htmlFor={`${idPrefix}-rate`}>Base Rate</label>
              <input
                id={`${idPrefix}-rate`}
                inputMode="decimal"
                placeholder="12000.00"
                value={form.baseRate}
                onChange={(e) => patch({ baseRate: e.target.value })}
              />
            </div>
            <div className="console-field">
              <label htmlFor={`${idPrefix}-currency`}>Currency</label>
              <select
                id={`${idPrefix}-currency`}
                value={form.currency}
                onChange={(e) => patch({ currency: e.target.value })}
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className="console-field">
              <label htmlFor={`${idPrefix}-rpname`}>Rate Plan Name</label>
              <input
                id={`${idPrefix}-rpname`}
                value={form.ratePlanName}
                onChange={(e) => patch({ ratePlanName: e.target.value })}
              />
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}

/** Convert a unit form into the shared create/update field payload. */
function unitFieldPayload(form: UnitFormState): {
  name: string;
  type: string;
  countOfRooms: number;
  maxAdults: number;
  maxChildren: number;
  maxInfants: number;
  defaultOccupancy: number;
  maxOccupancy: number;
  roomKind: "room" | "dorm";
  amenities: readonly string[];
} {
  const adults = Number(form.maxAdults) || 1;
  return {
    name: form.name.trim(),
    type: form.type.toLowerCase(),
    countOfRooms: Number(form.countOfRooms) || 1,
    maxAdults: adults,
    maxChildren: Number(form.maxChildren) || 0,
    maxInfants: Number(form.maxInfants) || 0,
    defaultOccupancy: Number(form.defaultOccupancy) || 1,
    maxOccupancy: adults,
    roomKind: form.type.toLowerCase() === "dorm" ? "dorm" : "room",
    amenities: form.amenities
      .split(",")
      .map((a) => a.trim())
      .filter((a) => a !== ""),
  };
}

// ===========================================================================
// Add unit drawer (right-side, §7/§8/§28)
// ===========================================================================

function AddUnitDrawer({
  propertyId,
  onClose,
  onCreated,
}: {
  readonly propertyId: string;
  readonly onClose: () => void;
  readonly onCreated: (note: string) => void;
}): ReactElement {
  const [form, setForm] = useState<UnitFormState>(emptyUnitForm);
  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<UserMessageModel | null>(null);

  function patch(next: Partial<UnitFormState>): void {
    setForm((prev) => ({ ...prev, ...next }));
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setApiError(null);
    const invalid = validateUnitForm(form);
    if (invalid !== null) {
      setFieldError(invalid);
      return;
    }
    setFieldError(null);
    const fields = unitFieldPayload(form);
    const rateMinor =
      form.createRatePlan && form.baseRate.trim() !== ""
        ? parseMoneyToMinor(form.baseRate)
        : 0;
    setBusy(true);
    const result = await apiCreateUnit({
      propertyId,
      ...fields,
      ...(form.createRatePlan
        ? {
            createRatePlan: true,
            ratePlanName: form.ratePlanName.trim() || "Standard",
            baseRateMinor: rateMinor ?? 0,
            baseRateCurrency: form.currency.trim() || "INR",
          }
        : { baseRateMinor: rateMinor ?? 0, baseRateCurrency: form.currency.trim() || "INR" }),
    });
    setBusy(false);
    const err = asError(result);
    if (err !== null) {
      setApiError(toUserMessage(err));
      return;
    }
    const ok = result as UnitMutationResult;
    const synced = ok.sync?.status === "synced";
    onCreated(
      synced ? "Unit created and synced." : "Unit created · Channex sync pending.",
    );
  }

  return (
    <div
      className="prop-drawer-scrim"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form
        className="prop-drawer units-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Add unit"
        onSubmit={handleSubmit}
        noValidate
      >
        <header className="prop-drawer__header">
          <div>
            <p className="prop-drawer__eyebrow">Inventory</p>
            <h2 className="prop-drawer__title">Add unit</h2>
          </div>
          <button
            type="button"
            className="console-btn console-btn--ghost prop-drawer__close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>
        <div className="prop-drawer__body">
          {apiError !== null ? (
            <UserMessage message={apiError} severity="error" />
          ) : null}
          {fieldError !== null ? (
            <UserMessage
              message={{
                what: fieldError,
                why: "Complete the required fields before creating the unit.",
              }}
              severity="error"
            />
          ) : null}
          <UnitFormFields form={form} patch={patch} idPrefix="add-unit" />
        </div>
        <div className="prop-drawer__footer">
          <button
            type="button"
            className="console-btn console-btn--ghost"
            onClick={onClose}
          >
            Cancel
          </button>
          <button type="submit" className="console-btn" disabled={busy}>
            {busy ? "Creating unit…" : "Create unit"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ===========================================================================
// Manage unit drawer (tabbed: Overview / Rate Plans / Inventory / Integration)
// ===========================================================================

type ManageTab = "overview" | "rateplans" | "inventory" | "integration";

function ManageUnitDrawer({
  unit,
  onClose,
  onSaved,
}: {
  readonly unit: Unit;
  readonly onClose: () => void;
  readonly onSaved: (note: string) => void;
}): ReactElement {
  const [tab, setTab] = useState<ManageTab>("overview");
  const [form, setForm] = useState<UnitFormState>(() => unitFormFromUnit(unit));
  const [loaded, setLoaded] = useState<Unit>(unit);
  const [channexRoomTypeId, setChannexRoomTypeId] = useState<string | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<UserMessageModel | null>(null);
  const [integrationNote, setIntegrationNote] = useState<string | null>(null);

  // Prefill from the authoritative record + mapping (network → SSR-safe).
  useEffect(() => {
    let active = true;
    setLoading(true);
    apiGetUnit(unit.id)
      .then((res) => {
        if (!active) return;
        const err = asError(res);
        if (err !== null) {
          setApiError(toUserMessage(err));
          setLoading(false);
          return;
        }
        const ok = res as { unit: Unit; channexRoomTypeId: string | null };
        setLoaded(ok.unit);
        setForm(unitFormFromUnit(ok.unit));
        setChannexRoomTypeId(ok.channexRoomTypeId);
        setLoading(false);
      })
      .catch(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [unit.id]);

  function patch(next: Partial<UnitFormState>): void {
    setForm((prev) => ({ ...prev, ...next }));
  }

  async function handleSave(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setApiError(null);
    const invalid = validateUnitForm(form);
    if (invalid !== null) {
      setFieldError(invalid);
      return;
    }
    setFieldError(null);
    const fields = unitFieldPayload(form);
    const rateMinor =
      form.baseRate.trim() !== "" ? parseMoneyToMinor(form.baseRate) : null;
    setBusy(true);
    const result = await apiUpdateUnit({
      unitId: unit.id,
      ...fields,
      status: form.status,
      ...(rateMinor !== null
        ? { baseRateMinor: rateMinor, baseRateCurrency: form.currency.trim() || "INR" }
        : {}),
    });
    setBusy(false);
    const err = asError(result);
    if (err !== null) {
      setApiError(toUserMessage(err));
      return;
    }
    const ok = result as UnitMutationResult;
    onSaved(
      ok.sync?.status === "synced" ? "Saved · synced." : "Saved · sync pending.",
    );
  }

  async function handleSyncNow(): Promise<void> {
    setSyncBusy(true);
    setIntegrationNote(null);
    const res = await apiSyncUnit(unit.id);
    setSyncBusy(false);
    const err = asError(res);
    if (err !== null) {
      setApiError(toUserMessage(err));
      return;
    }
    const ok = res as { unitId: string; status: string };
    setIntegrationNote(
      ok.status === "synced" ? "Sync complete." : `Sync status: ${ok.status}.`,
    );
  }

  const tabs: readonly { readonly id: ManageTab; readonly label: string }[] = [
    { id: "overview", label: "Overview" },
    { id: "rateplans", label: "Rate Plans" },
    { id: "inventory", label: "Inventory" },
    { id: "integration", label: "Integration" },
  ];

  return (
    <div
      className="prop-drawer-scrim"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="prop-drawer units-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={`Manage ${loaded.name}`}
      >
        <header className="prop-drawer__header">
          <div>
            <p className="prop-drawer__eyebrow">Inventory</p>
            <h2 className="prop-drawer__title">Manage Unit — {loaded.name}</h2>
          </div>
          <button
            type="button"
            className="console-btn console-btn--ghost prop-drawer__close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>

        <div className="prop-drawer__tabs" role="tablist">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={
                tab === t.id
                  ? "prop-drawer__tab prop-drawer__tab--active"
                  : "prop-drawer__tab"
              }
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" ? (
          <form className="units-drawer__form" onSubmit={handleSave} noValidate>
            <div className="prop-drawer__body">
              {loading ? (
                <p className="console-loading" role="status" aria-live="polite">
                  Loading unit…
                </p>
              ) : (
                <>
                  {apiError !== null ? (
                    <UserMessage message={apiError} severity="error" />
                  ) : null}
                  {fieldError !== null ? (
                    <UserMessage
                      message={{
                        what: fieldError,
                        why: "Complete the required fields before saving.",
                      }}
                      severity="error"
                    />
                  ) : null}
                  <UnitFormFields
                    form={form}
                    patch={patch}
                    idPrefix="edit-unit"
                  />
                </>
              )}
            </div>
            <div className="prop-drawer__footer">
              <button
                type="button"
                className="console-btn console-btn--ghost"
                onClick={onClose}
              >
                Cancel
              </button>
              <button type="submit" className="console-btn" disabled={busy}>
                {busy ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        ) : null}

        {tab === "rateplans" ? (
          <div className="prop-drawer__body">
            <RatePlansPanel unit={loaded} />
          </div>
        ) : null}

        {tab === "inventory" ? (
          <div className="prop-drawer__body">
            <AvailabilityEditor unit={loaded} />
          </div>
        ) : null}

        {tab === "integration" ? (
          <div className="prop-drawer__body">
            <section className="prop-drawer__section">
              <h3 className="console-form__title">Channel connection</h3>
              <p className="prop-help">
                {channexRoomTypeId !== null
                  ? "Room Type Connected — this unit is mapped and distributing."
                  : "Not connected — sync to map this unit for distribution."}
              </p>
              <div className="bookings-drawer__pills">
                <span
                  className={
                    channexRoomTypeId !== null
                      ? "avos-status avos-status--ok"
                      : "avos-status"
                  }
                >
                  {channexRoomTypeId !== null
                    ? "Room Type Connected"
                    : "Not connected"}
                </span>
              </div>
              {channexRoomTypeId !== null ? (
                <details className="units-tech">
                  <summary>View technical details</summary>
                  <p className="prop-help">Room type ID: {channexRoomTypeId}</p>
                </details>
              ) : null}
              {integrationNote !== null ? (
                <p className="recon-caption">{integrationNote}</p>
              ) : null}
              <div className="units-actions">
                <button
                  type="button"
                  className="console-btn"
                  onClick={handleSyncNow}
                  disabled={syncBusy}
                >
                  {syncBusy ? "Syncing…" : "Sync now"}
                </button>
              </div>
            </section>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ===========================================================================
// Delete confirmation modal (§26)
// ===========================================================================

function DeleteUnitModal({
  unit,
  onCancel,
  onDeleted,
}: {
  readonly unit: Unit;
  readonly onCancel: () => void;
  readonly onDeleted: () => void;
}): ReactElement {
  const [busy, setBusy] = useState(false);
  const [apiError, setApiError] = useState<UserMessageModel | null>(null);

  async function handleDelete(): Promise<void> {
    setApiError(null);
    setBusy(true);
    const res = await apiDeleteUnit(unit.id);
    setBusy(false);
    const err = asError(res);
    if (err !== null) {
      setApiError(toUserMessage(err));
      return;
    }
    onDeleted();
  }

  return (
    <div
      className="console-modal-scrim"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className="console-modal console-modal--confirm"
        role="dialog"
        aria-modal="true"
        aria-label={`Delete ${unit.name}`}
      >
        <header className="console-modal__header">
          <h2 className="console-modal__title">Delete unit</h2>
          <button
            type="button"
            className="console-btn console-btn--ghost console-modal__close"
            aria-label="Close"
            onClick={onCancel}
          >
            <IconClose size={18} />
          </button>
        </header>
        <div className="console-modal__body">
          {apiError !== null ? (
            <UserMessage message={apiError} severity="error" />
          ) : null}
          <p>
            Permanently delete unit? This action may also remove the
            corresponding Channex room type and cannot be undone.
          </p>
          <div className="console-modal__actions">
            <button
              type="button"
              className="console-btn console-btn--ghost"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className="console-btn console-btn--gold"
              onClick={handleDelete}
              disabled={busy}
            >
              {busy ? "Deleting…" : "Delete permanently"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Rate-plan creation form
// ===========================================================================

function CreateRatePlanForm({
  unitId,
  currency,
  onCreated,
}: {
  readonly unitId: string;
  readonly currency: string;
  readonly onCreated: () => void;
}): ReactElement {
  const [name, setName] = useState("");
  const [rate, setRate] = useState("");
  const [minStay, setMinStay] = useState("");
  const [maxStay, setMaxStay] = useState("");
  const [cta, setCta] = useState(false);
  const [ctd, setCtd] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [apiError, setApiError] = useState<UserMessageModel | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setApiError(null);
    setOkMsg(null);
    if (name.trim() === "") {
      setNameError("Rate plan name is required.");
      return;
    }
    setNameError(null);
    // A rate plan must carry a rate — an empty rate is a mistake, not zero.
    if (rate.trim() === "") {
      setApiError({
        what: "Rate is required.",
        why: "A rate plan needs a nightly rate before it can distribute.",
        next: "Enter a rate (e.g. 12000.00) and try again.",
      });
      return;
    }
    const rateMinor = parseMoneyToMinor(rate);
    if (rateMinor === null) {
      setApiError({
        what: "Rate is invalid.",
        why: "Enter a non-negative decimal amount (e.g. 12000.00).",
        next: "Correct the rate and try again.",
      });
      return;
    }
    setBusy(true);
    const result = await apiCreateRatePlan({
      unitId,
      name: name.trim(),
      rateMinor,
      rateCurrency: currency,
      ...(minStay.trim() !== "" ? { minStay: Number(minStay) } : {}),
      ...(maxStay.trim() !== "" ? { maxStay: Number(maxStay) } : {}),
      closedToArrival: cta,
      closedToDeparture: ctd,
    });
    setBusy(false);
    const err = asError(result);
    if (err !== null) {
      setApiError(toUserMessage(err));
      return;
    }
    setOkMsg(`Rate plan “${name.trim()}” added.`);
    setName("");
    setRate("");
    setMinStay("");
    setMaxStay("");
    setCta(false);
    setCtd(false);
    onCreated();
  }

  return (
    <form className="console-form" onSubmit={handleSubmit} noValidate>
      <h4 className="console-form__title">Create rate plan</h4>
      {apiError !== null ? (
        <UserMessage message={apiError} severity="error" />
      ) : null}
      {okMsg !== null ? (
        <UserMessage
          message={{
            what: okMsg,
            why: "The unit now has a rate plan and can be distributed.",
          }}
          severity="status"
        />
      ) : null}
      <div className="console-form__row">
        <div className="console-field">
          <label htmlFor="rp-name">Name</label>
          <input
            id="rp-name"
            value={name}
            aria-invalid={nameError !== null}
            aria-describedby={nameError !== null ? "rp-name-error" : undefined}
            onChange={(e) => setName(e.target.value)}
          />
          {nameError !== null ? (
            <div id="rp-name-error">
              <UserMessage
                message={{ what: nameError, why: "This field is required." }}
                severity="error"
              />
            </div>
          ) : null}
        </div>
        <div className="console-field">
          <label htmlFor="rp-rate">Rate</label>
          <input
            id="rp-rate"
            inputMode="decimal"
            placeholder="10000.00"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
          />
        </div>
      </div>
      <div className="console-form__row">
        <div className="console-field">
          <label htmlFor="rp-min">Min stay</label>
          <input
            id="rp-min"
            type="number"
            min="0"
            value={minStay}
            onChange={(e) => setMinStay(e.target.value)}
          />
        </div>
        <div className="console-field">
          <label htmlFor="rp-max">Max stay</label>
          <input
            id="rp-max"
            type="number"
            min="0"
            value={maxStay}
            onChange={(e) => setMaxStay(e.target.value)}
          />
        </div>
        <div className="console-field console-field--checkbox">
          <label htmlFor="rp-cta">
            <input
              id="rp-cta"
              type="checkbox"
              checked={cta}
              onChange={(e) => setCta(e.target.checked)}
            />
            Closed to arrival
          </label>
          <label htmlFor="rp-ctd">
            <input
              id="rp-ctd"
              type="checkbox"
              checked={ctd}
              onChange={(e) => setCtd(e.target.checked)}
            />
            Closed to departure
          </label>
        </div>
      </div>
      <button type="submit" className="console-btn" disabled={busy}>
        {busy ? "Creating…" : "Create rate plan"}
      </button>
    </form>
  );
}

// ===========================================================================
// Availability editor
// ===========================================================================

function AvailabilityEditor({ unit }: { readonly unit: Unit }): ReactElement {
  const [days, setDays] = useState<readonly AvailabilityDay[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [day, setDay] = useState(isoDateOffset(0));
  const [isAvailable, setIsAvailable] = useState(true);
  const [priceOverride, setPriceOverride] = useState("");
  const [busy, setBusy] = useState(false);
  const [apiError, setApiError] = useState<UserMessageModel | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    apiListAvailability({
      unitId: unit.id,
      from: isoDateOffset(0),
      to: isoDateOffset(14),
    })
      .then((d) => {
        setDays(d);
        setLoading(false);
      })
      .catch(() => {
        setDays([]);
        setLoading(false);
      });
  }, [unit.id]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    apiListAvailability({
      unitId: unit.id,
      from: isoDateOffset(0),
      to: isoDateOffset(14),
    })
      .then((d) => {
        if (active) {
          setDays(d);
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setDays([]);
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [unit.id]);

  async function handleSave(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setApiError(null);
    let priceMinor: number | null = null;
    if (priceOverride.trim() !== "") {
      const parsed = parseMoneyToMinor(priceOverride);
      if (parsed === null) {
        setApiError({
          what: "Price override is invalid.",
          why: "Enter a non-negative decimal amount, or leave it blank.",
          next: "Correct the price and try again.",
        });
        return;
      }
      priceMinor = parsed;
    }
    setBusy(true);
    const result = await apiSetAvailability({
      unitId: unit.id,
      day,
      isAvailable,
      priceMinor,
    });
    setBusy(false);
    const err = asError(result);
    if (err !== null) {
      setApiError(toUserMessage(err));
      return;
    }
    setPriceOverride("");
    refresh();
  }

  return (
    <div className="console-subsection">
      <h4 className="console-form__title">Availability (next 14 days)</h4>
      <form className="console-form" onSubmit={handleSave} noValidate>
        {apiError !== null ? (
          <UserMessage message={apiError} severity="error" />
        ) : null}
        <div className="console-form__row">
          <div className="console-field">
            <label htmlFor="avail-day">Date</label>
            <input
              id="avail-day"
              type="date"
              value={day}
              onChange={(e) => setDay(e.target.value)}
            />
          </div>
          <div className="console-field console-field--checkbox">
            <label htmlFor="avail-open">
              <input
                id="avail-open"
                type="checkbox"
                checked={isAvailable}
                onChange={(e) => setIsAvailable(e.target.checked)}
              />
              Available
            </label>
          </div>
          <div className="console-field">
            <label htmlFor="avail-price">Price override (optional)</label>
            <input
              id="avail-price"
              inputMode="decimal"
              placeholder="e.g. 15000.00"
              value={priceOverride}
              onChange={(e) => setPriceOverride(e.target.value)}
            />
          </div>
        </div>
        <button type="submit" className="console-btn" disabled={busy}>
          {busy ? "Saving…" : "Save availability"}
        </button>
      </form>

      {loading ? (
        <p className="console-loading" role="status" aria-live="polite">
          Loading availability…
        </p>
      ) : days !== null && days.length > 0 ? (
        <table className="console-table">
          <caption className="console-sr-only">
            Availability for the next 14 days
          </caption>
          <thead>
            <tr>
              <th scope="col">Date</th>
              <th scope="col">Status</th>
              <th scope="col">Price override</th>
            </tr>
          </thead>
          <tbody>
            {days.map((d) => (
              <tr key={d.id}>
                <td>{d.day}</td>
                <td>
                  <span
                    className={
                      d.isAvailable
                        ? "console-badge console-badge--ok"
                        : "console-badge console-badge--off"
                    }
                  >
                    {d.isAvailable ? "Available" : "Closed"}
                  </span>
                </td>
                <td>
                  {d.priceMinor !== null
                    ? formatMoney(d.priceMinor, unit.baseRateCurrency)
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="console-empty">
          No availability records yet for the next 14 days. Set a date above to
          create one.
        </p>
      )}
    </div>
  );
}

// ===========================================================================
// Rate plans list
// ===========================================================================

function RatePlansPanel({ unit }: { readonly unit: Unit }): ReactElement {
  const [plans, setPlans] = useState<readonly RatePlan[] | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    apiListRatePlans(unit.id)
      .then((p) => {
        setPlans(p);
        setLoading(false);
      })
      .catch(() => {
        setPlans([]);
        setLoading(false);
      });
  }, [unit.id]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    apiListRatePlans(unit.id)
      .then((p) => {
        if (active) {
          setPlans(p);
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setPlans([]);
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [unit.id]);

  return (
    <div className="console-subsection">
      <h4 className="console-form__title">Rate plans</h4>
      {loading ? (
        <p className="console-loading" role="status" aria-live="polite">
          Loading rate plans…
        </p>
      ) : plans !== null && plans.length > 0 ? (
        <table className="console-table">
          <caption className="console-sr-only">
            Rate plans for {unit.name}
          </caption>
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Rate</th>
              <th scope="col">Min stay</th>
              <th scope="col">Max stay</th>
              <th scope="col">Restrictions</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((p) => (
              <tr key={p.id}>
                <td>{p.name}</td>
                <td>{formatMoney(p.rateMinor, p.rateCurrency)}</td>
                <td>{p.minStay ?? "—"}</td>
                <td>{p.maxStay ?? "—"}</td>
                <td>
                  {p.closedToArrival ? (
                    <span className="console-badge console-badge--off">CTA</span>
                  ) : null}{" "}
                  {p.closedToDeparture ? (
                    <span className="console-badge console-badge--off">CTD</span>
                  ) : null}
                  {!p.closedToArrival && !p.closedToDeparture ? "—" : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="console-empty">No rate plans yet for this unit.</p>
      )}
      <CreateRatePlanForm
        unitId={unit.id}
        currency={unit.baseRateCurrency}
        onCreated={refresh}
      />
    </div>
  );
}

// ===========================================================================
// Units panel (for a selected property)
// ===========================================================================

// ===========================================================================
// Expandable row detail (§23) — compact rate/channex/inventory mini-lines
// ===========================================================================

function UnitDetailRow({
  unit,
  sync,
}: {
  readonly unit: Unit;
  readonly sync: UnitSyncInfo | undefined;
}): ReactElement {
  const [plans, setPlans] = useState<readonly RatePlan[] | null>(null);
  const [loading, setLoading] = useState(true);

  // Lazy-load rate plans on first expand (network in an effect → SSR-safe).
  useEffect(() => {
    let active = true;
    setLoading(true);
    apiListRatePlans(unit.id)
      .then((p) => {
        if (active) {
          setPlans(p);
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setPlans([]);
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [unit.id]);

  const roomMapped = sync?.channexRoomTypeId != null;

  return (
    <div className="units-detail">
      <div className="units-detail__line">
        <span className="units-detail__label">Rate Plans</span>
        <span className="units-detail__value">
          {loading
            ? "Loading…"
            : plans !== null && plans.length > 0
              ? plans
                  .map(
                    (p) => `${p.name} · ${formatMoney(p.rateMinor, p.rateCurrency)}`,
                  )
                  .join("  ·  ")
              : "No rate plans yet"}
        </span>
      </div>
      <div className="units-detail__line">
        <span className="units-detail__label">Channex</span>
        <span className="units-detail__value">
          {roomMapped ? "Room mapped · Rate mapped" : "Not mapped"}
        </span>
      </div>
      <div className="units-detail__line">
        <span className="units-detail__label">Inventory</span>
        <span className="units-detail__value">
          Today: {unit.countOfRooms ?? 1} available
        </span>
      </div>
    </div>
  );
}

// ===========================================================================
// Row action menu (§24) — Manage + overflow (Edit / Sync / Archive / Delete)
// ===========================================================================

function UnitRowActions({
  unit,
  channexConnected,
  onManage,
  onEdit,
  onSync,
  onArchive,
  onDelete,
}: {
  readonly unit: Unit;
  readonly channexConnected: boolean;
  readonly onManage: () => void;
  readonly onEdit: () => void;
  readonly onSync: () => void;
  readonly onArchive: () => void;
  readonly onDelete: () => void;
}): ReactElement {
  return (
    <div className="units-actions">
      <button type="button" className="console-btn console-btn--ghost" onClick={onManage}>
        Manage
      </button>
      <details className="units-menu">
        <summary
          className="console-btn console-btn--ghost units-menu__trigger"
          aria-label={`More actions for ${unit.name}`}
        >
          <IconMore size={18} />
        </summary>
        <div className="units-menu__list" role="menu">
          <button type="button" role="menuitem" onClick={onEdit}>
            Edit
          </button>
          {channexConnected ? (
            <button type="button" role="menuitem" onClick={onSync}>
              Sync with Channex
            </button>
          ) : null}
          <button type="button" role="menuitem" onClick={onArchive}>
            Archive
          </button>
          <button
            type="button"
            role="menuitem"
            className="units-menu__danger"
            onClick={onDelete}
          >
            Delete
          </button>
        </div>
      </details>
    </div>
  );
}

// ===========================================================================
// Units panel (for a selected property) — list-focused (§6/§38)
// ===========================================================================

function UnitsPanel({
  property,
  autoOpenAdd = false,
  onAutoOpenAddConsumed,
}: {
  readonly property: Property;
  readonly autoOpenAdd?: boolean;
  readonly onAutoOpenAddConsumed?: () => void;
}): ReactElement {
  const [units, setUnits] = useState<readonly Unit[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncMap, setSyncMap] = useState<Map<string, UnitSyncInfo>>(new Map());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "archived">(
    "all",
  );

  const [addOpen, setAddOpen] = useState(false);
  const [manageUnit, setManageUnit] = useState<Unit | null>(null);
  const [deleteUnit, setDeleteUnit] = useState<Unit | null>(null);

  const [reconBusy, setReconBusy] = useState(false);
  const [reconLine, setReconLine] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [rowBusyId, setRowBusyId] = useState<string | null>(null);

  /** Fetch each unit's Channex mapping in parallel → derive a sync status. */
  const loadSyncMap = useCallback(async (list: readonly Unit[]): Promise<void> => {
    const entries = await Promise.all(
      list.map(async (u): Promise<readonly [string, UnitSyncInfo]> => {
        try {
          const res = await apiGetUnit(u.id);
          const err = asError(res);
          if (err !== null) {
            return [u.id, { channexRoomTypeId: null, status: "not_connected" }];
          }
          const ok = res as { unit: Unit; channexRoomTypeId: string | null };
          return [
            u.id,
            {
              channexRoomTypeId: ok.channexRoomTypeId,
              status: ok.channexRoomTypeId !== null ? "synced" : "not_connected",
            },
          ];
        } catch {
          return [u.id, { channexRoomTypeId: null, status: "not_connected" }];
        }
      }),
    );
    setSyncMap(new Map(entries));
  }, []);

  const refresh = useCallback(() => {
    setLoading(true);
    apiListUnits(property.id)
      .then((u) => {
        setUnits(u);
        setLoading(false);
        void loadSyncMap(u);
      })
      .catch(() => {
        setUnits([]);
        setLoading(false);
      });
  }, [property.id, loadSyncMap]);

  useEffect(() => {
    let active = true;
    setExpandedId(null);
    setLoading(true);
    apiListUnits(property.id)
      .then((u) => {
        if (!active) return;
        setUnits(u);
        setLoading(false);
        void loadSyncMap(u);
      })
      .catch(() => {
        if (active) {
          setUnits([]);
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [property.id, loadSyncMap]);

  // When the parent panel's "+ Add unit" header action fires, open the Add
  // drawer here (the room-creation flow lives in this panel). The parent lifts
  // the request as a flag so the single Add flow stays in one place.
  useEffect(() => {
    if (!autoOpenAdd) return;
    setBanner(null);
    setAddOpen(true);
    onAutoOpenAddConsumed?.();
  }, [autoOpenAdd, onAutoOpenAddConsumed]);

  // Property is Channex-connected if any unit carries a room-type mapping.
  const channexConnected = useMemo(
    () => Array.from(syncMap.values()).some((s) => s.channexRoomTypeId !== null),
    [syncMap],
  );

  const visibleUnits = useMemo(() => {
    if (units === null) return null;
    const q = search.trim().toLowerCase();
    return units.filter((u) => {
      const matchQ = q === "" || u.name.toLowerCase().includes(q);
      const unitStatus = u.status === "archived" ? "archived" : "active";
      const matchStatus = statusFilter === "all" || unitStatus === statusFilter;
      return matchQ && matchStatus;
    });
  }, [units, search, statusFilter]);

  async function handleReconcile(): Promise<void> {
    setReconBusy(true);
    setReconLine(null);
    const res = await apiReconcileInventory(property.id);
    setReconBusy(false);
    const err = asError(res);
    if (err !== null) {
      setReconLine("Sync failed — please try again.");
      return;
    }
    const s = res as InventoryReconcileSummary;
    if (s.notConnected) {
      setReconLine("This property is not connected to a channel yet.");
      return;
    }
    const roomTypes = s.roomsCreated + s.roomsUpdated + s.roomsAlreadySynced;
    setReconLine(
      `Inventory synced · ${roomTypes} room types · ${s.ratePlansCreated} rate plans · ${s.availabilityDaysSynced} days availability`,
    );
    refresh();
  }

  async function handleFixSync(unit: Unit): Promise<void> {
    setRowBusyId(unit.id);
    const res = await apiSyncUnit(unit.id);
    setRowBusyId(null);
    const err = asError(res);
    if (err !== null) {
      setBanner(toUserMessage(err).what);
      return;
    }
    refresh();
  }

  async function handleArchive(unit: Unit): Promise<void> {
    setRowBusyId(unit.id);
    const res = await apiArchiveUnit(unit.id);
    setRowBusyId(null);
    const err = asError(res);
    if (err !== null) {
      setBanner(toUserMessage(err).what);
      return;
    }
    setBanner(`“${unit.name}” archived.`);
    refresh();
  }

  return (
    <section className="console-panel" aria-label={`Units for ${property.name}`}>
      <div className="units-head">
        <h2 className="console-panel__title">Units — {property.name}</h2>
        <div className="units-head__actions">
          <button
            type="button"
            className="console-btn console-btn--ghost"
            onClick={handleReconcile}
            disabled={reconBusy}
          >
            {reconBusy ? "Syncing…" : "Sync inventory"}
          </button>
          <button
            type="button"
            className="console-btn units-head__add"
            aria-label="+ Add unit"
            onClick={() => {
              setBanner(null);
              setAddOpen(true);
            }}
          >
            <IconPlus size={15} />
            <span>Add unit</span>
          </button>
        </div>
      </div>

      {reconLine !== null ? (
        <p className="recon-caption">{reconLine}</p>
      ) : null}
      {banner !== null ? (
        <UserMessage message={{ what: banner, why: "" }} severity="status" />
      ) : null}

      {units !== null && units.length > 0 ? (
        <div className="units-filters">
          <div className="console-field">
            <label htmlFor="units-search" className="console-sr-only">
              Search units
            </label>
            <input
              id="units-search"
              placeholder="Search units…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="console-field">
            <label htmlFor="units-status" className="console-sr-only">
              Filter by status
            </label>
            <select
              id="units-status"
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(
                  e.target.value === "active"
                    ? "active"
                    : e.target.value === "archived"
                      ? "archived"
                      : "all",
                )
              }
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>
      ) : null}

      {loading ? (
        <p className="console-loading" role="status" aria-live="polite">
          Loading units…
        </p>
      ) : visibleUnits !== null && visibleUnits.length > 0 ? (
        <table className="console-table units-table">
          <caption className="console-sr-only">Units for {property.name}</caption>
          <thead>
            <tr>
              <th scope="col" className="units-table__chevcol">
                <span className="console-sr-only">Expand</span>
              </th>
              <th scope="col">Name</th>
              <th scope="col">Type</th>
              <th scope="col">Inventory</th>
              <th scope="col">Occupancy</th>
              <th scope="col">Rate plan</th>
              <th scope="col">Channex Sync</th>
              <th scope="col">Status</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleUnits.map((u) => {
              const sync = syncMap.get(u.id);
              const syncStatus = sync?.status ?? "not_connected";
              const pill = syncPill(syncStatus);
              const isArchived = u.status === "archived";
              const isExpanded = expandedId === u.id;
              const busyRow = rowBusyId === u.id;
              return (
                <Fragment key={u.id}>
                  <tr>
                    <td className="units-table__chevcol">
                      <button
                        type="button"
                        className="units-chevron"
                        aria-expanded={isExpanded}
                        aria-label={
                          isExpanded
                            ? `Collapse ${u.name}`
                            : `Expand ${u.name}`
                        }
                        onClick={() =>
                          setExpandedId(isExpanded ? null : u.id)
                        }
                      >
                        <svg
                          viewBox="0 0 16 16"
                          width="12"
                          height="12"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M6 4l4 4-4 4" />
                        </svg>
                      </button>
                    </td>
                    <td>{u.name}</td>
                    <td>{accommodationLabel(u.type)}</td>
                    <td>{inventorySummary(u)}</td>
                    <td>{occupancySummary(u)}</td>
                    <td className="units-table__rateplan">
                      <span className="units-rateplan__name">Base rate</span>
                      <span className="units-rateplan__price">
                        {formatMoney(u.baseRateMinor, u.baseRateCurrency)}
                      </span>
                    </td>
                    <td>
                      <span className={pill.cls}>{pill.label}</span>
                      {syncStatus !== "synced" && channexConnected ? (
                        <button
                          type="button"
                          className="console-btn console-btn--ghost units-fixsync"
                          onClick={() => handleFixSync(u)}
                          disabled={busyRow}
                        >
                          {busyRow ? "…" : "Fix sync"}
                        </button>
                      ) : null}
                    </td>
                    <td>
                      <span
                        className={
                          isArchived
                            ? "avos-status"
                            : "avos-status avos-status--ok"
                        }
                      >
                        {isArchived ? "Archived" : "Active"}
                      </span>
                    </td>
                    <td>
                      <UnitRowActions
                        unit={u}
                        channexConnected={channexConnected}
                        onManage={() => setManageUnit(u)}
                        onEdit={() => setManageUnit(u)}
                        onSync={() => handleFixSync(u)}
                        onArchive={() => handleArchive(u)}
                        onDelete={() => setDeleteUnit(u)}
                      />
                    </td>
                  </tr>
                  {isExpanded ? (
                    <tr className="units-detail-row">
                      <td colSpan={9}>
                        <UnitDetailRow unit={u} sync={sync} />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      ) : units !== null && units.length > 0 ? (
        <p className="console-empty">No units match your search.</p>
      ) : (
        <p className="console-empty">
          No units yet for this property. Use “+ Add unit” to create the first
          one.
        </p>
      )}

      {addOpen ? (
        <AddUnitDrawer
          propertyId={property.id}
          onClose={() => setAddOpen(false)}
          onCreated={(note) => {
            setAddOpen(false);
            setBanner(note);
            refresh();
          }}
        />
      ) : null}

      {manageUnit !== null ? (
        <ManageUnitDrawer
          unit={manageUnit}
          onClose={() => setManageUnit(null)}
          onSaved={(note) => {
            setManageUnit(null);
            setBanner(note);
            refresh();
          }}
        />
      ) : null}

      {deleteUnit !== null ? (
        <DeleteUnitModal
          unit={deleteUnit}
          onCancel={() => setDeleteUnit(null)}
          onDeleted={() => {
            const name = deleteUnit.name;
            setDeleteUnit(null);
            setBanner(`“${name}” deleted.`);
            refresh();
          }}
        />
      ) : null}
    </section>
  );
}

// ===========================================================================
// Top-level screen
// ===========================================================================

/** Drawer intent: closed, creating, or editing a specific property. */
type DrawerMode =
  | { readonly kind: "closed" }
  | { readonly kind: "create" }
  | { readonly kind: "edit"; readonly propertyId: string };

// ===========================================================================
// Property list presentation — summary cards + a visual card grid (default)
// with an optional dense table view. UI-only: derived from existing data.
// ===========================================================================

/** Human label for a property type (title-cased; underscores → spaces). */
function propertyTypeLabel(type: string | null): string {
  if (type === null || type.trim() === "") return "Property";
  return type
    .split("_")
    .map((w) => (w.length > 0 ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(" ");
}

// The property thumbnail tile uses a single neutral IconBuilding SVG for every
// property type (directive 28: no per-type EMOJI). The tile is purely a visual
// anchor for the name — the type is already stated as text in the row's second
// line ("Location · Property type"), so no per-type differentiation is needed.

/** A short location string from a property's city/country (or an em dash). */
function propertyLocation(p: Property): string {
  const city = p.city !== null ? p.city.trim() : "";
  if (city !== "") {
    return p.country !== null && p.country.trim() !== ""
      ? `${city}, ${p.country}`
      : city;
  }
  return "Location not set";
}

/**
 * One property rendered as a hierarchical, inline-expandable panel (Stage 3,
 * directives A + O). The collapsed header row shows identity, distribution
 * status, unit count and connected-channel chips, a "Manage" button (opens the
 * edit drawer) and a chevron. Expanding renders the EXISTING `UnitsPanel` (the
 * room table) INLINE directly beneath the header — no detached bottom section,
 * no navigate-away. Single-expand is driven by the parent's `selectedId`.
 */
function PropertyPanel({
  property,
  distribution,
  isExpanded,
  onToggle,
  onManage,
  onAddRoom,
  autoOpenAdd,
  onAutoOpenAddConsumed,
}: {
  readonly property: Property;
  readonly distribution: PropertyDistribution | null;
  readonly isExpanded: boolean;
  readonly onToggle: () => void;
  readonly onManage: () => void;
  /** Expand the panel (if needed) and open its Add-room drawer. */
  readonly onAddRoom: () => void;
  /** When true, the nested room panel should auto-open its Add drawer. */
  readonly autoOpenAdd: boolean;
  /** Called once the nested panel has consumed the auto-open request. */
  readonly onAutoOpenAddConsumed: () => void;
}): ReactElement {
  const state: DistributionState = distribution?.distributionState ?? "not_ready";
  const channels = distribution?.connectedChannels ?? [];
  const unitCount = distribution?.unitCount ?? 0;
  const panelBodyId = `prop-panel-body-${property.id}`;
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <article
      className={
        isExpanded ? "prop-panel prop-panel--expanded" : "prop-panel"
      }
    >
      <div className="prop-panel__head">
        {/* The whole collapsed row is clickable to expand/collapse; the
            chevron carries the accessible Expand/Collapse name. */}
        <button
          type="button"
          className="prop-panel__row"
          aria-expanded={isExpanded}
          aria-controls={panelBodyId}
          aria-label={
            isExpanded ? `Collapse ${property.name}` : `Expand ${property.name}`
          }
          onClick={onToggle}
        >
          <span className="prop-panel__chevron" aria-hidden="true">
            <IconChevron size={16} />
          </span>
          <span className="prop-panel__glyph" aria-hidden="true">
            <IconBuilding size={18} />
          </span>
          <span className="prop-panel__ident-text">
            <span className="prop-panel__name">{property.name}</span>
            <span className="prop-panel__location">
              {propertyLocation(property)}
              <span className="prop-panel__dot" aria-hidden="true">
                ·
              </span>
              {propertyTypeLabel(property.type)}
            </span>
          </span>

          <span className="prop-panel__meta">
            <span className={distributionStatusClass(state)}>
              {distributionStatusLabel(state)}
            </span>
            <span className="prop-panel__units">
              {unitCount} {unitCount === 1 ? "room" : "rooms"}
            </span>
            <span className="prop-panel__dot" aria-hidden="true">
              ·
            </span>
            {channels.length > 0 ? (
              <span className="prop-panel__chips">
                {channels.map((c) => (
                  <span key={c} className="prop-chip">
                    {c}
                  </span>
                ))}
              </span>
            ) : (
              <span className="prop-panel__chips prop-panel__chips--none">
                No channels connected
              </span>
            )}
          </span>
        </button>

        <div className="prop-panel__actions">
          <button
            type="button"
            className="console-btn console-btn--ghost prop-panel__action prop-panel__action--add"
            onClick={onAddRoom}
          >
            <IconPlus size={14} />
            <span>Add unit</span>
          </button>
          <button
            type="button"
            className="console-btn console-btn--ghost prop-panel__action"
            aria-label="Manage"
            onClick={onManage}
          >
            Edit property
          </button>
          <div className="prop-panel__overflow">
            <button
              type="button"
              className="console-btn console-btn--ghost prop-panel__overflow-trigger"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label={`More actions for ${property.name}`}
              onClick={() => setMenuOpen((v) => !v)}
            >
              <IconMore size={18} />
            </button>
            {menuOpen ? (
              <>
                <div
                  className="prop-panel__overflow-backdrop"
                  aria-hidden="true"
                  onClick={() => setMenuOpen(false)}
                />
                <div className="prop-panel__overflow-menu" role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      onManage();
                    }}
                  >
                    Edit property
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      onAddRoom();
                    }}
                  >
                    Add unit
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="prop-panel__overflow-disabled"
                    disabled
                    title="Archive is available from the Edit property drawer’s Danger zone."
                  >
                    Archive property
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>

      {isExpanded ? (
        <div id={panelBodyId} className="prop-panel__body">
          <UnitsPanel
            property={property}
            autoOpenAdd={autoOpenAdd}
            onAutoOpenAddConsumed={onAutoOpenAddConsumed}
          />
        </div>
      ) : null}
    </article>
  );
}

// ===========================================================================
// Archived (decommissioned) properties — the "Archived" tab (requirement 14)
// and its read-only detail (requirement 15/16/17/20). Decommissioned properties
// never appear in the Active list (the backend `apiListProperties` already
// filters them); these views are entirely read-only and render NO mutating
// controls. SSR-safe: all network is inside effects/handlers.
// ===========================================================================

/** Format a date-ish string to a short label (shared with the archived views). */
function formatArchivedDate(value: string | null): string {
  if (value === null || value.trim() === "") return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** The read-only detail panel for a single archived property. */
function ArchivedPropertyDetailPanel({
  propertyId,
  onBack,
}: {
  readonly propertyId: string;
  readonly onBack: () => void;
}): ReactElement {
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<ArchivedPropertyDetail | null>(null);
  const [loadError, setLoadError] = useState<UserMessageModel | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(null);
    apiArchivedPropertyDetail(propertyId)
      .then((res) => {
        if (!active) return;
        const err = asError(res);
        if (err !== null) {
          setDetail(null);
          setLoadError(toUserMessage(err));
          setLoading(false);
          return;
        }
        const candidate = (res as { detail?: unknown } | null | undefined)?.detail;
        if (
          candidate === null ||
          candidate === undefined ||
          typeof candidate !== "object" ||
          !Array.isArray((candidate as { reservations?: unknown }).reservations) ||
          !Array.isArray((candidate as { units?: unknown }).units)
        ) {
          setDetail(null);
          setLoadError({
            what: "Unable to load this archived property. Please try again.",
            why: "The archived property detail could not be read from the server.",
            next: "Choose Try again. If this persists, contact support.",
          });
          setLoading(false);
          return;
        }
        setDetail(candidate as ArchivedPropertyDetail);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setDetail(null);
        setLoadError({
          what: "Unable to load this archived property. Please try again.",
          why: "The archived property detail could not be read from the server.",
          next: "Choose Try again. If this persists, contact support.",
        });
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [propertyId, reloadTick]);

  if (loading) {
    return (
      <p className="console-loading" role="status" aria-live="polite">
        Loading archived property…
      </p>
    );
  }

  if (loadError !== null) {
    return (
      <div className="prop-archived__detail">
        <button type="button" className="console-btn console-btn--ghost" onClick={onBack}>
          ← Back to archived
        </button>
        <UserMessage message={loadError} severity="error" />
        <button
          type="button"
          className="console-btn"
          onClick={() => setReloadTick((t) => t + 1)}
        >
          Try again
        </button>
      </div>
    );
  }

  if (detail === null) {
    return (
      <div className="prop-archived__detail">
        <button type="button" className="console-btn console-btn--ghost" onClick={onBack}>
          ← Back to archived
        </button>
        <UserMessage
          message={{
            what: "Archived property details are unavailable.",
            why: "The detail could not be loaded.",
            next: "Go back and try again.",
          }}
          severity="error"
        />
      </div>
    );
  }

  const units = detail.units ?? [];
  const reservations = detail.reservations ?? [];
  const guests = detail.guests ?? [];
  const channels = detail.channels ?? [];
  const revenueMinor = Number(detail.historicalRevenueMinor);
  const location =
    [detail.address, detail.city, detail.country].filter(
      (p) => p !== null && p.trim() !== "",
    ).join(", ") || "—";

  return (
    <div className="prop-archived__detail">
      <button type="button" className="console-btn console-btn--ghost" onClick={onBack}>
        ← Back to archived
      </button>

      <div className="prop-archived__detail-head">
        <h2 className="console-panel__title">{detail.name}</h2>
        <span className="prop-chip prop-archived__badge">Archived / Decommissioned</span>
      </div>

      <UserMessage
        message={{
          what: "This property is archived and read-only.",
          why: `Decommissioned ${formatArchivedDate(detail.decommissionedAt)}. Its history is retained for reference.`,
          next: "No action required — records are kept for your reference.",
        }}
        severity="status"
      />

      <dl className="prop-archived__meta">
        <div>
          <dt>Location</dt>
          <dd>{location}</dd>
        </div>
        <div>
          <dt>Decommissioned</dt>
          <dd>{formatArchivedDate(detail.decommissionedAt)}</dd>
        </div>
        <div>
          <dt>Historical revenue</dt>
          <dd>
            {Number.isNaN(revenueMinor)
              ? "—"
              : formatMoney(
                  revenueMinor,
                  reservations[0]?.currency ?? "INR",
                )}
          </dd>
        </div>
        <div>
          <dt>Channels / sources</dt>
          <dd>
            {channels.length > 0
              ? channels.map((c) => (
                  <span key={c} className="prop-chip">
                    {c}
                  </span>
                ))
              : "—"}
          </dd>
        </div>
      </dl>

      {/* Units / rooms */}
      <section className="prop-archived__section">
        <h3 className="prop-drawer__section-title">Units &amp; rooms ({units.length})</h3>
        {units.length > 0 ? (
          <ul className="prop-archived__units">
            {units.map((u) => (
              <li key={u.unitId}>
                {u.name ?? "Unnamed unit"}{" "}
                <span className="prop-chip">{u.status}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="prop-help">No units recorded.</p>
        )}
      </section>

      {/* Historical reservations — ALL statuses incl. cancelled / no-show */}
      <section className="prop-archived__section">
        <h3 className="prop-drawer__section-title">
          Reservation history ({reservations.length})
        </h3>
        {reservations.length > 0 ? (
          <table className="console-table">
            <caption className="console-sr-only">Historical reservations</caption>
            <thead>
              <tr>
                <th scope="col">Guest</th>
                <th scope="col">Unit</th>
                <th scope="col">Dates</th>
                <th scope="col">Status</th>
                <th scope="col">Source</th>
                <th scope="col">Reference</th>
                <th scope="col">Value</th>
              </tr>
            </thead>
            <tbody>
              {reservations.map((r: ArchivedReservationRow) => {
                const amount = Number(r.amountMinor);
                const ref =
                  (r.confirmationCode !== null && r.confirmationCode.trim() !== ""
                    ? r.confirmationCode
                    : r.otaReservationCode) ?? "—";
                return (
                  <tr key={r.reservationId}>
                    <td>{r.guestName ?? "—"}</td>
                    <td>{r.unitName ?? "—"}</td>
                    <td>
                      {formatArchivedDate(r.checkIn)} – {formatArchivedDate(r.checkOut)}
                    </td>
                    <td>
                      <span className="prop-chip">{r.status}</span>
                    </td>
                    <td>{r.channel !== "" ? r.channel : r.source}</td>
                    <td>{ref}</td>
                    <td className="recon-num">
                      {Number.isNaN(amount) ? "—" : formatMoney(amount, r.currency)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="prop-help">No reservations recorded.</p>
        )}
      </section>

      {/* Guest history */}
      <section className="prop-archived__section">
        <h3 className="prop-drawer__section-title">Guest history ({guests.length})</h3>
        {guests.length > 0 ? (
          <ul className="prop-archived__guests">
            {guests.map((g) => (
              <li key={g.guestId}>
                {g.name ?? "Unnamed guest"}
                {g.email !== null && g.email.trim() !== "" ? (
                  <span className="prop-help"> · {g.email}</span>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="prop-help">No guests recorded.</p>
        )}
      </section>
    </div>
  );
}

/** The Archived-tab list + drill-in to the read-only detail. */
function ArchivedPropertiesPanel(): ReactElement {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<readonly ArchivedPropertyRow[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    apiListArchivedProperties()
      .then((list) => {
        if (!active) return;
        setRows(list);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setRows([]);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  if (selectedId !== null) {
    return (
      <ArchivedPropertyDetailPanel
        propertyId={selectedId}
        onBack={() => setSelectedId(null)}
      />
    );
  }

  if (loading) {
    return (
      <p className="console-loading" role="status" aria-live="polite">
        Loading archived properties…
      </p>
    );
  }

  if (rows === null || rows.length === 0) {
    return (
      <p className="console-empty">
        No archived properties. Decommissioned properties appear here with their retained history.
      </p>
    );
  }

  return (
    <table className="console-table">
      <caption className="console-sr-only">Archived properties</caption>
      <thead>
        <tr>
          <th scope="col">Name</th>
          <th scope="col">Location</th>
          <th scope="col">Decommissioned</th>
          <th scope="col">View</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((p) => {
          const location =
            p.city !== null && p.city.trim() !== ""
              ? `${p.city}${p.country !== null ? `, ${p.country}` : ""}`
              : "—";
          return (
            <tr key={p.id}>
              <td>
                {p.name}{" "}
                <span className="prop-chip prop-archived__badge">Archived</span>
              </td>
              <td>{location}</td>
              <td>{formatArchivedDate(p.decommissionedAt)}</td>
              <td>
                <button
                  type="button"
                  className="console-btn console-btn--ghost"
                  onClick={() => setSelectedId(p.id)}
                >
                  View details
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default function PropertiesScreen(): ReactElement {
  const [properties, setProperties] = useState<readonly Property[] | null>(
    null,
  );
  const [distribution, setDistribution] = useState<
    readonly PropertyDistribution[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // When set, the matching property's nested room panel auto-opens its Add
  // drawer (driven by the header "+ Add unit" action).
  const [addRoomFor, setAddRoomFor] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<DrawerMode>({ kind: "closed" });
  const [note, setNote] = useState<DistributionNote | null>(null);
  // Top-level Active | Archived tab (requirement 14). Default "active" — the
  // existing active list is unchanged; "archived" mounts the read-only panel.
  const [tab, setTab] = useState<"active" | "archived">("active");

  const refresh = useCallback(() => {
    setLoading(true);
    Promise.all([apiListProperties(), apiPropertyDistribution()])
      .then(([p, d]) => {
        setProperties(p);
        setDistribution(d);
        setLoading(false);
      })
      .catch(() => {
        setProperties([]);
        setDistribution([]);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    let active = true;
    Promise.all([apiListProperties(), apiPropertyDistribution()])
      .then(([p, d]) => {
        if (active) {
          setProperties(p);
          setDistribution(d);
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setProperties([]);
          setDistribution([]);
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const distByProperty = new Map(
    distribution.map((d) => [d.propertyId, d] as const),
  );

  // Derived summary totals (from existing data — no backend change). "Setup
  // Required" aggregates every non-ready, non-provisioning state from the
  // reconciled 6-state set (draft / setup_required / sync_failed / legacy).
  const totals = {
    properties: properties?.length ?? 0,
    units: distribution.reduce((sum, d) => sum + d.unitCount, 0),
    ready: distribution.filter((d) => d.distributionState === "ready").length,
    setupRequired: distribution.filter((d) =>
      [
        "draft",
        "setup_required",
        "not_ready",
        "sync_failed",
        "error",
      ].includes(d.distributionState),
    ).length,
  };

  function handleSaved(
    savedNote: DistributionNote,
    opts?: { readonly keepOpen?: boolean },
  ): void {
    // Edit saves keep the drawer open (compact sync-result line); create/draft
    // saves close it. Either way, surface the note + refresh the list.
    if (opts?.keepOpen !== true) setDrawer({ kind: "closed" });
    setNote(savedNote);
    refresh();
  }

  // Feature A: after a property is removed (deleted or decommissioned), close
  // the drawer, clear the selection if the removed property was the active one
  // (so no broken drill-in / refresh loop, A18), surface a confirmation note,
  // and refresh the list (the removed property drops off the normal list).
  function handleRemoved(
    outcome: "deleted" | "decommissioned",
    name: string,
    manualCleanupNote?: string | null,
  ): void {
    setDrawer({ kind: "closed" });
    const removedId = selectedId;
    if (removedId !== null) setSelectedId(null);
    // When manual cleanup / the Booking.com extranet step remains, show the
    // truthful follow-up note (as a status) instead of a plain success — the
    // removal still completed locally, but the operator has a remaining step.
    const hasManualNote =
      typeof manualCleanupNote === "string" && manualCleanupNote.trim() !== "";
    if (hasManualNote) {
      setNote({
        severity: "info",
        message: {
          what:
            outcome === "deleted"
              ? `“${name}” was removed, but a manual Channex step remains.`
              : `“${name}” was decommissioned, but a manual Channex step remains.`,
          why: manualCleanupNote as string,
          next: "Complete the manual step in Channex / the OTA extranet to finish removal.",
        },
      });
      refresh();
      return;
    }
    setNote({
      severity: "success",
      message:
        outcome === "deleted"
          ? {
              what: `“${name}” was permanently deleted.`,
              why: "The property had no bookings or history, so it was removed completely.",
              next: "It no longer appears in your portfolio.",
            }
          : {
              what: `“${name}” was decommissioned and archived.`,
              why: "The property had history, so its reservations, guests and records are retained.",
              next: "It is removed from your active portfolio and no longer syncs with channels.",
            },
    });
    refresh();
  }

  return (
    <section
      aria-labelledby="properties-heading"
      data-console-screen="properties"
    >
      <div className="prop-header-row">
        <div>
          <p className="console-page-eyebrow">Portfolio · Inventory</p>
          <h1 id="properties-heading">Properties &amp; Inventory</h1>
          <p className="console-lede">
            Your properties, units, rate plans and availability in one place —
            the inventory every booking draws from.
          </p>
        </div>
        <button
          type="button"
          className="console-btn prop-header-row__cta"
          aria-label="+ Add property"
          onClick={() => {
            setNote(null);
            setDrawer({ kind: "create" });
          }}
        >
          <IconPlus size={15} />
          <span>Add property</span>
        </button>
      </div>

      {note !== null ? (
        <UserMessage
          message={note.message}
          severity={note.severity === "warning" ? "error" : "status"}
        />
      ) : null}

      {/* ---- Active | Archived tabs (requirement 14) ---- */}
      <div
        className="prop-viewtoggle prop-lifecycle-tabs"
        role="group"
        aria-label="Property lifecycle view"
      >
        <button
          type="button"
          className={
            tab === "active"
              ? "prop-viewtoggle__btn prop-viewtoggle__btn--active"
              : "prop-viewtoggle__btn"
          }
          aria-pressed={tab === "active"}
          onClick={() => {
            setNote(null);
            setTab("active");
          }}
        >
          Active
        </button>
        <button
          type="button"
          className={
            tab === "archived"
              ? "prop-viewtoggle__btn prop-viewtoggle__btn--active"
              : "prop-viewtoggle__btn"
          }
          aria-pressed={tab === "archived"}
          onClick={() => {
            setNote(null);
            setSelectedId(null);
            setTab("archived");
          }}
        >
          Archived
        </button>
      </div>

      {tab === "archived" ? (
        <section className="console-panel" aria-label="Archived properties">
          <div className="accounts-panel-head">
            <h2 className="console-panel__title">Archived properties</h2>
          </div>
          <ArchivedPropertiesPanel />
        </section>
      ) : (
      <>
      {/* ---- Top summary cards (derived from existing data) ---- */}
      {!loading && properties !== null && properties.length > 0 ? (
        <div className="console-card-grid prop-summary-cards">
          <div className="console-card console-card--gold prop-summary-card">
            <p className="console-card__label">Total Properties</p>
            <p className="console-card__value">{totals.properties}</p>
            <p className="console-card__hint">Across your portfolio</p>
          </div>
          <div className="console-card prop-summary-card">
            <p className="console-card__label">Total Units</p>
            <p className="console-card__value">{totals.units}</p>
            <p className="console-card__hint">Bookable units</p>
          </div>
          <div className="console-card prop-summary-card">
            <p className="console-card__label">Distribution Ready</p>
            <p className="console-card__value">{totals.ready}</p>
            <p className="console-card__hint">Connected &amp; live</p>
          </div>
          <div className="console-card prop-summary-card">
            <p className="console-card__label">Setup Required</p>
            <p className="console-card__value">{totals.setupRequired}</p>
            <p className="console-card__hint">Need attention</p>
          </div>
        </div>
      ) : null}

      <section className="console-panel" aria-label="Properties">
        <div className="accounts-panel-head">
          <h2 className="console-panel__title">Properties</h2>
        </div>

        {loading ? (
          <p className="console-loading" role="status" aria-live="polite">
            Loading properties…
          </p>
        ) : properties !== null && properties.length > 0 ? (
          // Hierarchical, inline-expandable panels (Stage 3, directives A + O).
          // Single-expand keyed off `selectedId`; the room table renders INLINE
          // beneath the header — there is NO detached bottom units section.
          <div className="prop-panel-list">
            {properties.map((p) => (
              <PropertyPanel
                key={p.id}
                property={p}
                distribution={distByProperty.get(p.id) ?? null}
                isExpanded={p.id === selectedId}
                onToggle={() =>
                  setSelectedId((cur) => (cur === p.id ? null : p.id))
                }
                onManage={() => {
                  setNote(null);
                  setDrawer({ kind: "edit", propertyId: p.id });
                }}
                onAddRoom={() => {
                  // Expand this property (so its room panel mounts) and request
                  // the nested panel to open its Add-room drawer.
                  setSelectedId(p.id);
                  setAddRoomFor(p.id);
                }}
                autoOpenAdd={addRoomFor === p.id}
                onAutoOpenAddConsumed={() => setAddRoomFor(null)}
              />
            ))}
          </div>
        ) : (
          <p className="console-empty">
            No properties yet. Use “+ Add property” to create your first one.
          </p>
        )}
      </section>
      </>
      )}

      {drawer.kind !== "closed" ? (
        <PropertyDrawer
          mode={drawer.kind}
          propertyId={drawer.kind === "edit" ? drawer.propertyId : null}
          onClose={() => setDrawer({ kind: "closed" })}
          onSaved={handleSaved}
          onRemoved={handleRemoved}
        />
      ) : null}
    </section>
  );
}
